<script setup>
/* eslint-disable no-alert */
import {
  computed,
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { accessProfileMessage } from 'dashboard/helper/accessProfileNotice';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import parsePhoneNumber from 'libphonenumber-js';
import aceleresIntegrations from 'dashboard/api/aceleres/integrations';
import linkedinIntegration from 'dashboard/api/aceleres/linkedin';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  clearIntegracoesPageData,
  getIntegracoesPageFailure,
  getIntegracoesPageFailureMessage,
  getIntegracoesPageData,
  prefetchIntegracoesPageData,
} from './integracoesPageData';
import wameSocialChannel from 'dashboard/api/channel/wameSocialChannel';
import { normalizeAceleresConnectionType } from 'dashboard/constants/aceleresConnectionTypes';
import { canAccessAppsAutomations } from 'dashboard/helper/appsAutomationsAccess';
import { disconnectAlertMutedLabel } from 'dashboard/helper/aceleresDisconnectedInstanceAlert';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { dynamicTimeLocalized } from 'shared/helpers/timeHelper';
import WhatsappConnectionWizard from './WhatsappConnectionWizard.vue';
import SocialConnectionWizard from './SocialConnectionWizard.vue';
import ChannelConnectionCard from './ChannelConnectionCard.vue';
import EmailIntegrationPanel from './EmailIntegrationPanel.vue';
import WhatsappKeysPanel from './WhatsappKeysPanel.vue';
import WhatsappKeyRequestDialog from './WhatsappKeyRequestDialog.vue';
import WhatsappContractDialog from './WhatsappContractDialog.vue';
import ManagedInboxesPanel from './ManagedInboxesPanel.vue';

const store = useStore();
const { accountScopedRoute } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');
const currentAccount = useMapGetter('getCurrentAccount');

// Mesmo gate de settings/agents/Index.vue: com <keep-alive> o header
// teleportado sobrevive a saida da rota e apareceria em outra pagina.
const isPageActive = ref(true);

const loading = ref(true);
const whatsappLoadFailed = ref(false);
const whatsappLoadFailureMessage = ref('');
const feedback = ref('');
const feedbackType = ref('success');
const feedbackDialog = ref(null);
const feedbackTitle = computed(() =>
  feedbackType.value === 'error'
    ? 'Não foi possível concluir'
    : feedbackType.value === 'groups-updated'
      ? 'Grupos atualizados'
      : feedbackType.value === 'groups-notice'
        ? 'Sincronização de grupos'
        : 'Tudo certo'
);
const actionLoading = reactive({});
const instances = ref([]);
const wamePool = ref({ configured: 0, available: 0 });
const keyState = ref(null);
const viewerIsManager = ref(false);
const viewerCanManageInboxes = ref(false);
const keyRequestOpen = ref(false);
const contractRequestOpen = ref(false);
const createWizardRef = ref(null);
const keyRequestError = ref('');
let keyRefreshTimer = null;
const keyUsed = computed(() => keyState.value?.mine?.used ?? 0);
const keyLimit = computed(() => keyState.value?.mine?.limit ?? 0);
const keyFreeSlots = computed(() =>
  Math.max(0, keyLimit.value - keyUsed.value)
);
const keyFree = computed(() => keyState.value?.free ?? 0);
const pendingKeyRequest = computed(() => keyState.value?.mine?.pending || null);
const poolInfoOpen = ref(false);
const loadKeyState = async () => {
  try {
    keyState.value = await aceleresIntegrations.getKeyEntitlements();
    return true;
  } catch {
    /* Preserve existing connections if quota retrieval is unavailable. */
    return false;
  }
};
const openNewWhatsapp = async () => {
  keyRequestError.value = '';
  if (!(await loadKeyState())) {
    keyRequestError.value =
      'Não foi possível consultar suas chaves. Tente novamente.';
    return;
  }
  if (!viewerIsManager.value && pendingKeyRequest.value) {
    keyRequestOpen.value = true;
    return;
  }
  if (keyFreeSlots.value > 0) {
    createWizardRef.value?.open();
    return;
  }
  if (viewerIsManager.value && keyFree.value > 0) {
    try {
      const mine = keyState.value.people.find(
        p => p.id === Number(keyState.value.mine.user_id)
      );
      if (mine) {
        keyState.value = await aceleresIntegrations.distributeKeys([
          { id: mine.id, limit: mine.limit + 1 },
        ]);
        createWizardRef.value?.open();
        return;
      }
    } catch (e) {
      keyRequestError.value = e.message;
      return;
    }
  }
  if (
    viewerIsManager.value &&
    keyState.value.contract_request?.target_total < keyState.value.contracted
  ) {
    contractRequestOpen.value = true;
    return;
  }
  keyRequestOpen.value = true;
};
const sellers = ref([]);
const teamOptions = ref([]);
const teamSearch = ref('');
const groupSearch = ref('');
// Providers habilitados pelo Aceleres. A lista era fixa aqui (API 1/2/Z-API)
// e oferecia a Evolution como padrão mesmo onde ela já foi descontinuada.
const whatsappProviders = ref([]);
const newConnectionDialog = ref(null);
// Seletor global; cada opção abre o assistente específico do canal escolhido.
const selectedInstanceId = ref(null);
const qrConnection = ref(null);
const groupConfig = ref(null);
let groupSettingsSession = 0;
let groupEditRevision = 0;
// Retrato do que o servidor confirmou como salvo. A tela precisa dele para
// saber o que MUDOU: sem uma referencia, "desmarquei tres grupos" e
// indistinguivel de "abri a tela", e as duas coisas tem consequencias muito
// diferentes -- salvar uma remocao ENCERRA as conversas daqueles grupos no
// atendimento (purgeGroupConversations, no Aceleres).
const persistedGroupConfig = ref(null);
let groupRequestSequence = 0;
const migration = ref(null);
const viewerCanCreate = ref(false);
const viewerIsSupervisor = ref(false);
const deletionPreview = ref(null);
const deletionDialog = ref(null);
const releaseConfirmation = ref(null);
const keyReleaseDialog = ref(null);
const actionConfirmation = ref(null);
const actionConfirmationDialog = ref(null);
const socialRemovalDialog = ref(null);
const socialRemovalPreview = ref(null);
const socialRemovalCode = ref('');
const settingsDialog = ref(null);

const mobileAlertPanelClass = [
  'max-md:gap-4',
  'max-md:p-4',
  'max-md:[&>div:first-child]:min-w-0',
  'max-md:[&_h3]:break-words',
  'max-md:[&>div:last-child]:flex-col-reverse',
  'max-md:[&>div:last-child]:gap-2',
];

let qrPollingTimer = null;
let qrPollingInFlight = false;
let qrRefreshTimer = null;
let livePollingTimer = null;
let livePollingInFlight = false;
let livePollingTick = 0;
let migrationRequestSequence = 0;
const whatsappStatusRetryAt = new Map();

const canPollWhatsappStatus = instanceId =>
  Date.now() >= (whatsappStatusRetryAt.get(instanceId) || 0);

const deferWhatsappStatus = (instanceId, data) => {
  if (data.code !== 'PROVIDER_RATE_LIMITED') return;
  const seconds = Number(data.retry_after);
  const delay = Number.isFinite(seconds) && seconds > 0 ? seconds : 60;
  whatsappStatusRetryAt.set(instanceId, Date.now() + delay * 1000);
};

// Cards em atividade são reconferidos a cada ciclo; os ociosos custam uma
// consulta ao provedor por instância e entram a cada 6 ciclos (~1min).
const LIVE_POLL_INTERVAL_MS = 10000;
const IDLE_POLL_EVERY_TICKS = 6;
const ACTIVE_STATUSES = [
  'connected',
  'connecting',
  'failed',
  'migration_pending',
];
const IDLE_STATUSES = ['disconnected', 'unknown'];
// O QR da API 2 expira em ~40s; renovamos antes disso enquanto ninguém leu.
const QR_REFRESH_MS = 30000;
// Releitura dos canais sociais enquanto algum está no meio de uma conexão.
const SOCIAL_POLL_INTERVAL_MS = 15000;
let socialPollingTimer = null;
let socialPollingInFlight = false;

const editForm = reactive({
  display_name: '',
  connection_type: 'individual_own',
  default_assignee_user_id: '',
  connection_api: 'api2',
  origin_team_id: '',
  team_ids: [],
});

const originEligibleTeamOptions = computed(() =>
  teamOptions.value.filter(team => team.origin_eligible)
);

const filteredTeamOptions = computed(() => {
  const query = teamSearch.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return teamOptions.value;

  return teamOptions.value.filter(team =>
    [team.name, ...(team.member_names || [])]
      .join(' ')
      .toLocaleLowerCase('pt-BR')
      .includes(query)
  );
});

// A lista de grupos de uma conexao antiga passa de 100 itens dentro de uma
// caixa de 224px: sem busca, marcar um grupo especifico vira rolagem cega.
const filteredGroupOptions = computed(() => {
  const groups = groupConfig.value?.available_groups || [];
  const query = groupSearch.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return groups;

  return groups.filter(group =>
    `${group.subject || ''} ${group.group_jid || ''}`
      .toLocaleLowerCase('pt-BR')
      .includes(query)
  );
});

const connectionTypes = [
  { value: 'individual_own', label: 'Individual próprio' },
  { value: 'individual_company', label: 'Individual da empresa' },
  { value: 'shared', label: 'Compartilhado' },
];

const TABS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: 'i-ri-whatsapp-line' },
  { key: 'social', label: 'Canais sociais', icon: 'i-lucide-share-2' },
  { key: 'chaves', label: 'Chaves de instância', icon: 'i-lucide-key-round' },
  { key: 'inboxes', label: 'Inboxes', icon: 'i-lucide-inbox' },
  { key: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { key: 'apps', label: 'Apps e automações', icon: 'i-lucide-blocks' },
];
// Chaves e Inboxes são exclusivas do gestor, inclusive na seleção pela URL.
// A área "Apps e automações": o módulo não entrou na operação e fica
// restrito a um operador único, pela mesma régua de e-mail que guarda a rota.
const canSeeApps = computed(() => canAccessAppsAutomations());
const visibleTabs = computed(() =>
  TABS.filter(
    tab =>
      (tab.key !== 'apps' || canSeeApps.value) &&
      (tab.key !== 'chaves' || viewerIsManager.value) &&
      (tab.key !== 'inboxes' || viewerCanManageInboxes.value)
  )
);
// A aba entra na URL para que "Canais Sociais" no menu lateral possa apontar
// direto para cá. Sem isso existiam duas telas concorrentes de canais sociais —
// e a do menu ainda descrevia o OAuth direto com a Meta, que deixou de existir
// quando Instagram e Messenger passaram para a WAME.
const route = useRoute();
const router = useRouter();

const isKnownTab = key => visibleTabs.value.some(tab => tab.key === key);
const activeTab = ref(
  isKnownTab(route.query.aba) ? route.query.aba : 'whatsapp'
);

const selectTab = async key => {
  if (!isKnownTab(key)) return;
  activeTab.value = key;
  if (route.query.aba === key) return;

  await router.replace({
    name: route.name,
    params: route.params,
    query: { ...route.query, aba: key },
  });
};

watch(
  () => route.query.aba,
  tab => {
    activeTab.value = isKnownTab(tab) ? tab : 'whatsapp';
  }
);

// Gradientes de marca — cosméticos, só no selo do ícone. Estado (conectado,
// falha, pendente) continua vindo exclusivamente dos tokens n-* (I9 da skill
// aceleres-zoraxy-integracoes: n-green não existe neste design system).
//
// As duas cores de cada gradiente foram escolhidas para dar >=4.5:1 de
// contraste contra o ícone branco (WCAG 2.1 SC 1.4.3/1.4.11) — os tons
// "oficiais" das marcas (verde WhatsApp, laranja/rosa Instagram, ciano
// TikTok, azul claro Telegram) ficam entre 1.4:1 e 2.8:1 com branco em cima,
// então usamos os tons mais escuros de cada marca em vez do gradiente
// completo. Ver auditoria completa na memória do projeto.
// Verde chapado amostrado da referencia enviada pelo utilizador: a mediana do
// interior do tile e #1f9757 (a amostra anterior, #0f9147, saiu de um pixel de
// borda e ficava mais escura que a imagem). Contra o glifo branco da 3,78:1,
// acima do minimo de 3:1 da WCAG 2.1 SC 1.4.11 para elemento grafico.
const WHATSAPP_ICON_GRADIENT = 'bg-[#1f9757] shadow-sm';

const EMAIL_CHANNEL = {
  key: 'email',
  label: 'E-mail',
  icon: 'i-lucide-mail',
  gradient: 'bg-[linear-gradient(135deg,#b91c1c,#7f1d1d)]',
};

const socialCatalog = [
  {
    key: 'instagram',
    label: 'Instagram',
    channelType: INBOX_TYPES.INSTAGRAM,
    route: 'instagram',
    icon: 'i-ri-instagram-line',
    gradient: 'bg-[linear-gradient(135deg,#833ab4,#c13584)]',
    themeKey: 'instagram',
  },
  {
    key: 'facebook',
    label: 'Messenger',
    channelType: INBOX_TYPES.FB,
    route: 'facebook',
    icon: 'i-ri-messenger-line',
    gradient: 'bg-[linear-gradient(135deg,#0a58ca,#1e3a8a)]',
    themeKey: 'messenger',
  },
  // Não é canal de conversa nem passa pela WAME: conecta a Company Page da
  // empresa por OAuth direto com a API oficial. Sem caixa de entrada, por isso
  // `channelType` nulo. Azul da marca contra o glifo branco dá 5,6:1.
  {
    key: 'linkedin',
    label: 'LinkedIn',
    channelType: null,
    route: 'linkedin',
    icon: 'i-ri-linkedin-box-fill',
    gradient: 'bg-[linear-gradient(135deg,#0a66c2,#004182)]',
    themeKey: 'linkedin',
  },
  {
    key: 'telegram',
    label: 'Telegram',
    channelType: INBOX_TYPES.TELEGRAM,
    route: 'telegram',
    icon: 'i-ri-telegram-line',
    gradient: 'bg-[linear-gradient(135deg,#075985,#0c4a6e)]',
    themeKey: 'telegram',
  },
];

// `?canal=` é como o retorno do LinkedIn devolve a pessoa já na rede certa.
const activeSocialKey = ref(
  socialCatalog.some(channel => channel.key === route.query.canal)
    ? route.query.canal
    : socialCatalog[0].key
);

// Remover arquiva: tudo que a inbox tem vira historico preservado. A unica
// excecao e o numero pessoal, onde a conversa que nunca teve atendente e dado
// privado e e expurgada — nesse caso ela nao entra na conta do arquivado.
const archivedConversationCount = computed(() => {
  const preview = deletionPreview.value;
  if (!preview) return 0;
  const assigned = Number(preview.assigned_count || 0);
  const unassigned = Number(preview.unassigned_count || 0);
  return preview.purges_unassigned ? assigned : assigned + unassigned;
});

const selectedInstance = computed(() =>
  instances.value.find(instance => instance.id === selectedInstanceId.value)
);

const connectionSearch = ref('');

// Busca por nome, numero (com ou sem mascara) e rotulo da conexao.
const matchesConnectionSearch = instance => {
  const query = connectionSearch.value.trim().toLowerCase();
  if (!query) return true;

  const digits = query.replace(/\D/g, '');
  const haystack = [instance.name, instance.label, instance.phone_number]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (haystack.includes(query)) return true;

  return Boolean(
    digits &&
      String(instance.phone_number || '')
        .replace(/\D/g, '')
        .includes(digits)
  );
};

const filteredInstances = computed(() =>
  instances.value.filter(matchesConnectionSearch)
);

const whatsappSections = computed(() => {
  const buckets = {
    personal: [],
    assigned: [],
    shared: [],
  };

  filteredInstances.value.forEach(instance => {
    const type = normalizeAceleresConnectionType(instance.connection_type);
    if (type === 'individual_own') buckets.personal.push(instance);
    else if (type === 'individual_company') buckets.assigned.push(instance);
    else buckets.shared.push(instance);
  });

  // Cada grupo se anuncia por um ÍCONE, não por um número. O "01/02/03" antigo
  // era fixo por tipo e as seções vazias somem, então uma empresa só com
  // números compartilhados via um "03" solitário ao lado de dois cards — e um
  // número encostado numa lista curta é lido como quantidade: quem via "03"
  // com 2 cards concluía que a tela escondia uma conexão. Renumerar resolveria
  // a leitura errada e manteria um ordinal que não significa nada aqui: os
  // grupos não são etapas de um passo a passo, são naturezas de conexão.
  //
  // Os ícones repetem de propósito o vocabulário do próprio card (uma pessoa =
  // responsável individual, grupo = equipe compartilhada, em whatsappDetails),
  // para o cabeçalho e o conteúdo do grupo dizerem a mesma coisa.
  return [
    {
      key: 'personal',
      icon: 'i-lucide-lock-keyhole',
      title: 'Minhas conexões',
      description:
        'Privadas: somente você vê e administra estas configurações.',
      instances: buckets.personal,
    },
    {
      key: 'assigned',
      icon: 'i-lucide-user-round',
      title: 'Números individuais da empresa',
      description: 'Números da empresa vinculados a um responsável específico.',
      instances: buckets.assigned,
    },
    {
      key: 'shared',
      icon: 'i-lucide-users-round',
      title: 'Números compartilhados',
      description:
        'Visíveis para quem participa da operação; só criador ou gestor administra.',
      instances: buckets.shared,
    },
  ].filter(section => section.instances.length);
});

// Espelho do que o Aceleres registrou para estes canais. A tela do Aceleres
// mostra esse estado; aqui ele faltava, e um canal conectado no Zoraxy podia
// estar fora do espelho sem ninguém perceber.
const mirroredSocialChannels = ref([]);
const socialWizardRef = ref(null);
const socialWizardError = ref('');
const socialWizardInboxId = ref(null);
const instagramIdentityDialog = ref(null);
const instagramIdentityCard = ref(null);
const instagramIdentityUsername = ref('');
const instagramIdentityError = ref('');

// Instagram e Messenger têm fonte de verdade própria: `wame/social_channels`.
// Ela é a única que sabe distinguir "autorizado" de "recebendo", porque a WAME
// só entrega o id da conta social no primeiro evento — nem a inbox nem o espelho
// do Aceleres carregam esse estado. Antes esta aba lia só a lista de inboxes e
// carimbava "Conectado" em qualquer registro existente, o que mostrava verde
// para um canal que jamais tinha completado o OAuth.
const wameChannels = ref([]);
const wameLoadFailed = ref(false);

// Estado da Company Page do LinkedIn da empresa (uma por empresa), lido do
// executor Laravel pela ponte do Rails.
const linkedinState = ref(null);
const linkedinLoadFailed = ref(false);
const { isAdmin } = useAdmin();
// A ponte do Rails exige administrador e o executor exige gestor: o botão só
// aparece quando os dois deixariam a ação passar.
const viewerCanManageLinkedin = computed(
  () => isAdmin.value && Boolean(linkedinState.value?.viewer_can_manage)
);
// Ações locais dos canais WAME (identificar @, desconectar) vivem no Chatwoot.
// `viewerIsManager` vem do payload WhatsApp do Aceleres; se essa ponte falhar
// (conta sem vínculo na empresa), o administrador do Zoraxy ainda precisa
// operar Instagram/Messenger já provisionados.
const canManageWameChannelActions = computed(
  () => viewerIsManager.value || isAdmin.value
);

// A rede se chama "facebook" no catálogo e "messenger" na WAME — o par abaixo é
// o que liga as duas nomenclaturas. Só estas duas redes passam pela WAME.
// Falta de conexão livre no pool. A reserva devolve um código por motivo
// (limite da homologação, pool compartilhado cheio, disputa, estado
// desconhecido) e o controller usa WAME_KEY_UNAVAILABLE como padrão. Antes só
// os dois últimos eram reconhecidos, e os demais caíam no caminho de erro
// genérico: o QR entrava em polling de 3s, a mensagem era substituída por
// "Sem resposta do servidor" e o loadHub redesenhava a tela antes de alguém
// terminar de ler o aviso.
const KEY_UNAVAILABLE_CODES = [
  'WAME_KEY_UNAVAILABLE',
  'WAME_SHARED_POOL_EXHAUSTED',
  'WAME_HOMOLOG_LIMIT_REACHED',
  'WAME_POOL_STATUS_UNKNOWN',
  'WAME_KEY_RESERVATION_RACE',
  'WAME_POOL_EXHAUSTED',
  'WAME_KEY_RELEASED',
];
const isKeyUnavailable = error => KEY_UNAVAILABLE_CODES.includes(error?.code);

const WAME_PROVIDER_BY_KEY = { instagram: 'instagram', facebook: 'messenger' };

const socialChannels = computed(() =>
  socialCatalog.map(channel => ({
    ...channel,
    records: (inboxes.value || []).filter(
      inbox => inbox.channel_type === channel.channelType
    ),
    mirror: mirroredSocialChannels.value.find(
      item => item.type === channel.key
    ),
  }))
);

// Conta canal de fato autorizado na Meta, não caixa de entrada existente: uma
// inbox criada e nunca autorizada contava como "canal social" e inflava o número
// no topo da página, o mesmo engano que a aba mostrava nos cartões.
const setFeedback = (message, type = 'success') => {
  feedbackDialog.value?.close();
  feedback.value = message;
  feedbackType.value = type;
  nextTick(() => feedbackDialog.value?.open());
};

const dismissFeedback = () => {
  feedbackDialog.value?.close();
  feedback.value = '';
};

const setLoading = (key, value) => {
  actionLoading[key] = value;
};

const stopQrPolling = () => {
  if (qrPollingTimer) {
    window.clearInterval(qrPollingTimer);
    qrPollingTimer = null;
  }
  if (qrRefreshTimer) {
    window.clearInterval(qrRefreshTimer);
    qrRefreshTimer = null;
  }
};

const statusLabel = (status, migrationStatus) => {
  const migrationLabels = {
    awaiting_confirmation: 'Revisão necessária',
    blocked: 'Consolidação bloqueada',
    queued: 'Consolidação na fila',
    running: 'Consolidação em andamento',
    recovery_pending: 'Retomada necessária',
  };
  const labels = {
    connected: 'Conectado',
    connecting: 'Conectando',
    disconnected: 'Desconectado',
    failed: 'Atenção necessária',
    migration_pending:
      migrationLabels[migrationStatus] || 'Consolidação pendente',
    // Estado remoto indeterminado: a API respondeu sem evidência de conexão
    // nem de queda. Não é o mesmo que "desconectado" e não deve parecer um.
    unknown: 'Verificando…',
    awaiting: 'Aguardando pareamento',
  };

  return labels[status] || 'Indisponível';
};

const migrationPhaseLabel = phase => {
  const labels = {
    preflight: 'Conferência das conexões e do histórico concluída',
    awaiting_confirmation: 'Revisão necessária antes de concluir',
    queued: 'Consolidação colocada na fila',
    running: 'Consolidação em andamento',
    recovery_pending: 'A consolidação precisa ser retomada',
    blocked: 'A consolidação está bloqueada',
    completed: 'Consolidação concluída',
    failed: 'Não foi possível concluir a consolidação',
  };

  return labels[phase] || phase || 'Aguardando atualização';
};

const migrationCardActionLabel = instance =>
  ['queued', 'running'].includes(instance.migration_status)
    ? 'Acompanhar consolidação'
    : 'Revisar consolidação';

const hasTeamAccess = teamId =>
  editForm.team_ids.includes(teamId) ||
  Number(editForm.origin_team_id) === teamId;

const isOriginTeam = teamId =>
  teamId === selectedInstance.value?.origin_team?.id ||
  (!selectedInstance.value?.origin_team &&
    Number(editForm.origin_team_id) === teamId);

const statusClass = status => {
  if (status === 'connected') return 'bg-n-teal-2 text-n-teal-11';
  if (['failed', 'migration_pending'].includes(status)) {
    return 'bg-n-ruby-2 text-n-ruby-11';
  }

  return 'bg-n-amber-2 text-n-amber-11';
};

// A WAME entrega o numero em E.164 sem o '+' (5551999999999). Sem o prefixo o
// libphonenumber nao identifica o pais; com ele, sai formatado por regiao.
// Numero fora do padrao volta como veio — nunca inventamos digito.
const formatPhoneNumber = value => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return value || '';

  try {
    const parsed = parsePhoneNumber(`+${digits}`);
    if (parsed?.isValid()) return parsed.formatInternational();
  } catch (error) {
    return value;
  }

  return value;
};

const connectionTypeLabel = type =>
  connectionTypes.find(
    item => item.value === normalizeAceleresConnectionType(type)
  )?.label || 'Não definido';

const connectionTechnologyLabel = value =>
  String(value || '').toLowerCase() === 'api2'
    ? 'Conexão Zoraxy'
    : value || 'Não identificada';

// dynamicTimeLocalized (date-fns) espera segundos unix, mas o bridge Laravel
// manda ISO8601 — converter aqui em vez de espalhar o cálculo pelos usos.
const toUnix = isoString =>
  isoString ? Math.floor(new Date(isoString).getTime() / 1000) : null;

// Responsável é `highlight`: é o que se procura primeiro no card e ganha a
// faixa do topo, ao lado do status. O resto desce para linhas rótulo → valor,
// que se leem em coluna alinhada em vez de blocos empilhados de larguras
// diferentes.
const whatsappDetails = instance => {
  // "Visível para" repetia a equipe de origem, que já está na linha acima —
  // duas linhas dizendo "beta team" faziam o bloco parecer mais bagunçado do
  // que era. Aqui sobra só o que a origem não conta: com quem ela foi
  // compartilhada. Sem ninguém além dela, a linha inteira desaparece.
  const sharedBeyondOrigin = (instance.shared_teams || [])
    .map(team => team.name)
    .filter(name => name && name !== instance.origin_team?.name);

  return [
    {
      label: 'Responsável',
      value:
        instance.responsibility_kind === 'shared'
          ? 'Equipe compartilhada'
          : instance.responsible_name || 'Responsável não definido',
      icon:
        instance.responsibility_kind === 'shared'
          ? 'i-lucide-users-round'
          : 'i-lucide-user-round',
      highlight: true,
      valueClass:
        instance.responsibility_kind === 'unassigned'
          ? 'text-n-ruby-11'
          : 'text-n-slate-12',
    },
    {
      label: 'Tipo de uso',
      value: connectionTypeLabel(instance.connection_type),
      icon: 'i-lucide-tag',
    },
    {
      label: 'Tecnologia',
      value: connectionTechnologyLabel(
        instance.connection_api || instance.provider
      ),
      icon: 'i-lucide-plug-zap',
    },
    ...(instance.origin_team
      ? [
          {
            label: 'Equipe de origem',
            value: instance.origin_team.name,
            icon: 'i-lucide-building-2',
          },
        ]
      : []),
    ...(sharedBeyondOrigin.length
      ? [
          {
            label: 'Compartilhada com',
            value: sharedBeyondOrigin.join(', '),
            icon: 'i-lucide-share-2',
          },
        ]
      : []),
  ];
};

const whatsappMetaLine = instance => {
  const parts = [];
  if (instance.original_manager?.name) {
    parts.push(`gestor original: ${instance.original_manager.name}`);
  } else if (instance.created_by) {
    parts.push(`criada por ${instance.created_by}`);
  }

  const sinceIso =
    instance.status === 'connected'
      ? instance.connected_at
      : instance.disconnected_at;
  const sinceUnix = toUnix(sinceIso);
  if (sinceUnix) {
    parts.push(
      `${instance.status === 'connected' ? 'conectado' : 'desconectado'} ${dynamicTimeLocalized(sinceUnix)}`
    );
  }

  return parts.join(' · ');
};

const SOCIAL_STATUS_VISUALS = {
  active: { label: 'Conectado', class: 'bg-n-teal-2 text-n-teal-11' },
  inactive: { label: 'Inativo', class: 'bg-n-amber-2 text-n-amber-11' },
};

// Uma linha por conexão real, não por rede. `channel.records` são inboxes
// nativas do Chatwoot (já na Vuex, sem fetch extra); `channel.mirror` é o
// espelho do Aceleres (WAME, mesma conexão do WhatsApp para IG/Messenger).
// Correção de bug: o backend nunca devolve status "connected", só
// 'active'/'inactive'/'not_connected' (OmnichannelChannel::isActive() checa
// 'active') — a comparação antiga nunca era verdadeira e escondia o nome da
// conta mesmo quando ela estava de fato conectada.
// Redes ainda não migradas para configuração dedicada no Zoraxy seguem
// administradas pelo Aceleres — o selo comunica isso em todo card da rede.
// Messenger entra pelo MESMO fluxo WAME do Instagram (Facebook.vue renderiza
// WameSocialChannel provider="messenger"), então marcá-lo "em desenvolvimento"
// era falso e escondia um canal que já funciona.
const networkStatusBadge = channel =>
  WAME_PROVIDER_BY_KEY[channel.key] ||
  ['linkedin', 'email'].includes(channel.key)
    ? { label: 'Disponível no Zoraxy', class: 'bg-n-teal-2 text-n-teal-11' }
    : { label: 'Migração em andamento', class: 'bg-n-amber-2 text-n-amber-11' };

const LINKEDIN_STATUS_VISUALS = {
  connected: { label: 'Conectado', class: 'bg-n-teal-2 text-n-teal-11' },
  expired: { label: 'Acesso expirado', class: 'bg-n-ruby-2 text-n-ruby-11' },
};

// Resultado do retorno do LinkedIn (`?linkedin=`). Só códigos viajam na URL; o
// texto mora aqui, para ninguém montar uma mensagem falsa num link.
const LINKEDIN_RESULT_MESSAGES = {
  connected: 'LinkedIn conectado. A Company Page da empresa está vinculada.',
  cancelled: 'A autorização do LinkedIn foi cancelada.',
  state_invalid:
    'O retorno do LinkedIn expirou ou já foi usado. Inicie a conexão novamente.',
  not_allowed: 'Somente gestores podem conectar o LinkedIn da empresa.',
  token_failed: 'O LinkedIn recusou a autorização. Tente conectar novamente.',
  page_not_found:
    'Esta conta do LinkedIn não administra nenhuma Company Page. Entre com um administrador da página.',
  unavailable: 'Não foi possível conectar o LinkedIn agora. Tente novamente.',
};

const buildLinkedinCards = (channel, networkBadge) => {
  const state = linkedinState.value;
  const base = {
    key: 'linkedin',
    icon: channel.icon,
    iconGradient: channel.gradient,
    badges: [networkBadge],
    channel,
  };

  if (!state) {
    return [
      {
        ...base,
        title: channel.label,
        subtitle: linkedinLoadFailed.value
          ? 'Estado indisponível'
          : 'Carregando…',
        notice: linkedinLoadFailed.value
          ? 'Não foi possível ler o estado do LinkedIn agora. Atualize a página.'
          : '',
        muted: true,
        // Sem isto o cartão cairia no botão das redes geridas pelo Acelerity,
        // que aponta para uma configuração de caixa de entrada que não existe.
        linkedin: { configured: false },
      },
    ];
  }

  if (!state.configured) {
    return [
      {
        ...base,
        title: channel.label,
        subtitle: 'Nenhuma Company Page conectada',
        statusLabel: 'Aguardando configuração',
        statusClass: 'bg-n-amber-2 text-n-amber-11',
        notice:
          'O aplicativo do LinkedIn ainda não foi configurado neste ambiente. Assim que as credenciais forem cadastradas, a conexão fica disponível aqui.',
        muted: true,
        linkedin: state,
      },
    ];
  }

  if (!state.has_saved_profile) {
    return [
      {
        ...base,
        title: channel.label,
        subtitle: 'Nenhuma Company Page conectada',
        notice:
          'Conecte a Company Page da empresa para receber no funil os leads dos formulários de anúncio. É preciso ser administrador da página no LinkedIn.',
        muted: true,
        linkedin: state,
      },
    ];
  }

  const visual =
    LINKEDIN_STATUS_VISUALS[state.status] || LINKEDIN_STATUS_VISUALS.expired;
  const connectedUnix = toUnix(state.connected_at);
  const expiresAt = state.expires_at ? new Date(state.expires_at) : null;

  return [
    {
      ...base,
      title: state.organization_name || 'Company Page',
      subtitle: 'Company Page',
      statusLabel: visual.label,
      statusClass: visual.class,
      metaLine: [
        connectedUnix ? `conectado ${dynamicTimeLocalized(connectedUnix)}` : '',
        state.connected && expiresAt
          ? `acesso vence em ${expiresAt.toLocaleDateString('pt-BR')}`
          : '',
      ]
        .filter(Boolean)
        .join(' · '),
      notice: state.connected
        ? ''
        : 'O acesso concedido pelo LinkedIn expirou. Reconecte a Company Page para voltar a receber leads.',
      muted: !state.connected,
      linkedin: state,
    },
  ];
};

// Os cinco estados que o backend distingue (WameChannel#wame_status). O rótulo
// diz o que a pessoa precisa saber para agir, não o nome interno do estado:
// "Aguardando 1ª mensagem" é uma espera legítima e não pede nenhuma ação.
const WAME_STATUS_VISUALS = {
  connected: {
    label: 'Recebendo mensagens',
    class: 'bg-n-teal-2 text-n-teal-11',
  },
  awaiting: {
    label: 'Aguardando 1ª mensagem',
    class: 'bg-n-amber-2 text-n-amber-11',
  },
  pending: {
    label: 'Autorização pendente',
    class: 'bg-n-amber-2 text-n-amber-11',
  },
  not_started: {
    label: 'Conexão não iniciada',
    class: 'bg-n-slate-2 text-n-slate-11',
  },
  disconnected: {
    label: 'Desconectado',
    class: 'bg-n-slate-2 text-n-slate-11',
  },
};

const WAME_STATUS_NOTICE = {
  awaiting:
    'Autorização concluída na Meta. A conta só é identificada quando a primeira mensagem chega — envie um direct de teste para confirmar a ponta a ponta.',
  pending:
    'A autorização foi iniciada, mas a Meta ainda não confirmou a conclusão. Retome a autorização para ativar o canal.',
  not_started:
    'Esta caixa de entrada já existia, mas não há registro de uma tentativa de autorização na Meta. Inicie a conexão quando quiser usar o canal.',
  disconnected:
    'O canal foi desligado no Zoraxy. A caixa de entrada e o histórico continuam preservados; autorize novamente para reativá-lo.',
};

// Um cartão por canal WAME real, com o estado que o backend de fato conhece.
const buildWameNotice = record => {
  if (record.reauthorization_required && record.status === 'connected') {
    return 'A Meta recusou o acesso em algum momento, mas a conexão continua carregando este canal. Use "Verificar agora"; se ele tiver parado mesmo, desconecte e conecte de novo.';
  }
  if (record.reauthorization_required) {
    return 'A Meta revogou o acesso desta conta. Refaça a autorização para voltar a receber.';
  }
  return WAME_STATUS_NOTICE[record.status] || '';
};

const buildWameCards = (channel, networkBadge) => {
  const provider = WAME_PROVIDER_BY_KEY[channel.key];
  const records = wameChannels.value.filter(item => item.provider === provider);

  if (!records.length) {
    return [
      {
        key: `empty-${channel.key}`,
        icon: channel.icon,
        iconGradient: channel.gradient,
        title: channel.label,
        subtitle: 'Nenhuma conta conectada',
        badges: [networkBadge],
        notice: `${channel.label} usa uma Conexão Zoraxy já disponível na empresa. A autorização é concluída em um fluxo protegido da Meta.`,
        muted: true,
        channel,
        wameProvider: provider,
      },
    ];
  }

  return records.map(record => {
    // Reautorização exigida vence o estado normal: é o único caso em que a
    // Meta revogou o acesso e nada volta a funcionar sem uma ação humana.
    const visual = record.reauthorization_required
      ? { label: 'Reautorização exigida', class: 'bg-n-ruby-2 text-n-ruby-11' }
      : WAME_STATUS_VISUALS[record.status] || WAME_STATUS_VISUALS.pending;
    const connectedUnix = toUnix(record.connected_at);
    const isInstagram = record.provider === 'instagram';
    const accountUsername = String(record.account_username || '').replace(
      /^@/,
      ''
    );
    let accountTitle = record.name;
    if (isInstagram) {
      accountTitle = accountUsername
        ? `@${accountUsername}`
        : 'Conta do Instagram não identificada';
    } else if (
      record.account_label &&
      record.account_label !== record.external_id
    ) {
      accountTitle = record.account_label;
    }
    const externalAccountMeta =
      !isInstagram && record.external_id ? `conta ${record.external_id}` : '';

    return {
      key: `wame-${record.inbox_id}`,
      icon: channel.icon,
      iconGradient: channel.gradient,
      // A WAME não devolve o perfil escolhido no OAuth e o primeiro evento só
      // fecha o vínculo pelo IGSID. Para Instagram, portanto, o título só usa um
      // @ confirmado; sem ele o card assume explicitamente que a conta ainda
      // não foi identificada. Inbox, conexão e id técnico são conceitos
      // separados — o último nem aparece para o usuário.
      title: accountTitle,
      subtitle: isInstagram ? record.name : 'Messenger',
      statusLabel: visual.label,
      statusClass: visual.class,
      badges: [networkBadge],
      details: [],
      metaLine: [
        externalAccountMeta,
        connectedUnix ? `conectado ${dynamicTimeLocalized(connectedUnix)}` : '',
      ]
        .filter(Boolean)
        .join(' · '),
      // "Reautorização exigida" + "conectado" ao mesmo tempo é um estado real e
      // já visto: o 403 antigo liga uma marca em Redis que nada desligava. Hoje
      // ela se cura sozinha (Wame::InstanceStateService limpa quando a chave
      // ainda carrega o canal), mas enquanto durar não adianta mandar refazer a
      // autorização — a página da WAME, para uma chave já autorizada, só sabe
      // dizer "conectado". O caminho honesto é verificar, e desconectar só se
      // tiver parado de verdade.
      notice: buildWameNotice(record),
      muted: ['pending', 'not_started', 'disconnected'].includes(record.status),
      channel,
      wameProvider: provider,
      wame: record,
    };
  });
};

const buildChannelCards = channel => {
  const networkBadge = networkStatusBadge(channel);

  // Instagram e Messenger não passam mais pela lista de inboxes nem pelo espelho
  // do Aceleres: nenhum dos dois distingue autorizado de recebendo.
  if (WAME_PROVIDER_BY_KEY[channel.key]) {
    return buildWameCards(channel, networkBadge);
  }

  if (channel.key === 'linkedin') {
    return buildLinkedinCards(channel, networkBadge);
  }

  const cards = [];
  const mirrorRecord = channel.mirror?.record;
  const mirrorVisual = SOCIAL_STATUS_VISUALS[channel.mirror?.status];
  if (mirrorVisual && mirrorRecord) {
    const sinceUnix = toUnix(
      mirrorRecord.status === 'active'
        ? mirrorRecord.connected_at
        : mirrorRecord.disconnected_at
    );
    cards.push({
      key: `mirror-${channel.key}`,
      icon: channel.icon,
      iconGradient: channel.gradient,
      title:
        mirrorRecord.external_account_name ||
        channel.mirror.account ||
        channel.label,
      subtitle: 'Gerenciado na central da Acelerity',
      statusLabel: mirrorVisual.label,
      statusClass: mirrorVisual.class,
      badges: [networkBadge],
      metaLine: sinceUnix
        ? `${mirrorRecord.status === 'active' ? 'conectado' : 'desconectado'} ${dynamicTimeLocalized(sinceUnix)}`
        : '',
      muted: mirrorRecord.status !== 'active',
      channel,
    });
  }

  if (!cards.length) {
    cards.push({
      key: `empty-${channel.key}`,
      icon: channel.icon,
      iconGradient: channel.gradient,
      title: channel.label,
      subtitle: 'Nenhuma conexão',
      badges: [networkBadge],
      notice: `A migração de ${channel.label} para o Zoraxy ainda está em andamento. Até a conclusão, use a central da Acelerity para gerenciar este canal.`,
      muted: true,
      channel,
    });
  }

  return cards;
};

// A sidebar controla um único painel por vez. Isso evita que a página role
// enquanto o indicador de vidro se move e mantém conteúdo, ícone e gradiente
// sincronizados até em saltos diretos do primeiro para o último canal.
const socialSections = computed(() =>
  socialChannels.value.map(channel => ({
    channel,
    cards: buildChannelCards(channel),
  }))
);

const activeSocialSection = computed(
  () =>
    socialSections.value.find(
      section => section.channel.key === activeSocialKey.value
    ) || socialSections.value[0]
);

const activeWameProvider = computed(() => {
  if (activeSocialKey.value === 'instagram') return 'instagram';
  if (activeSocialKey.value === 'facebook') return 'messenger';
  return '';
});

const openGlobalConnection = async provider => {
  newConnectionDialog.value?.close();

  if (provider === 'whatsapp') {
    await selectTab('whatsapp');
    await nextTick();
    await openNewWhatsapp();
    return;
  }

  await selectTab('social');
  activeSocialKey.value = provider === 'instagram' ? 'instagram' : 'facebook';
  await nextTick();
  openSocialWizard(provider);
};

const instagramIdentityProfileUrl = computed(() => {
  const username = instagramIdentityUsername.value.trim().replace(/^@+/, '');
  return /^[a-zA-Z0-9._]{1,30}$/.test(username)
    ? `https://www.instagram.com/${username}/`
    : '';
});

const openInstagramIdentity = card => {
  instagramIdentityCard.value = card;
  instagramIdentityUsername.value = card.wame.account_username || '';
  instagramIdentityError.value = '';
  instagramIdentityDialog.value?.open();
};

const saveInstagramIdentity = async () => {
  const card = instagramIdentityCard.value;
  const username = instagramIdentityUsername.value.trim().replace(/^@+/, '');

  if (!card || !/^[a-zA-Z0-9._]{1,30}$/.test(username)) {
    instagramIdentityError.value =
      'Informe um usuário válido do Instagram, sem espaços.';
    return;
  }

  const loadingKey = `instagram-identity-${card.wame.inbox_id}`;
  setLoading(loadingKey, true);
  instagramIdentityError.value = '';

  try {
    const { data } = await wameSocialChannel.identify(
      card.wame.inbox_id,
      username
    );
    wameChannels.value = wameChannels.value.map(channel =>
      channel.inbox_id === data.payload.inbox_id ? data.payload : channel
    );
    instagramIdentityDialog.value?.close();
    setFeedback('Conta do Instagram identificada.', 'success');
  } catch (error) {
    const status = error?.response?.status;
    if (!error?.response) {
      instagramIdentityError.value =
        'Sem conexão com o Zoraxy. Verifique sua internet e tente novamente.';
    } else if (status === 404) {
      instagramIdentityError.value =
        'Esta caixa de entrada não existe mais. Atualize a página e tente novamente.';
    } else if ([401, 403].includes(status)) {
      instagramIdentityError.value =
        'Você não tem permissão para identificar esta conta.';
    } else {
      instagramIdentityError.value =
        error.response.data?.error ||
        'Não foi possível salvar agora. Confira o perfil e tente novamente.';
    }
  } finally {
    setLoading(loadingKey, false);
  }
};

const verifyInstagramIdentity = async card => {
  const key = `instagram-verify-${card.wame.inbox_id}`;
  setLoading(key, true);
  try {
    await wameSocialChannel.verifyIdentity(card.wame.inbox_id);
    setFeedback(
      'Mensagem de verificação enviada. A identificação será atualizada quando o Instagram confirmar o perfil.',
      'success'
    );
  } catch (error) {
    setFeedback(
      error?.response?.data?.error || 'Não foi possível iniciar a verificação.',
      'error'
    );
  } finally {
    setLoading(key, false);
  }
};

const reusableSocialInboxes = computed(() =>
  wameChannels.value.filter(
    channel =>
      channel.provider === activeWameProvider.value &&
      ['not_started', 'pending', 'disconnected'].includes(channel.status)
  )
);

const selectSocialChannel = key => {
  activeSocialKey.value = key;
};

const updateInstanceStatus = (id, data) => {
  const status = data.status || data.api_status_raw;
  if (!status) return;

  instances.value = instances.value.map(instance =>
    instance.id === id
      ? {
          ...instance,
          status,
          migration_status:
            status === 'migration_pending'
              ? data.migration_status || instance.migration_status
              : null,
          migration_phase:
            status === 'migration_pending'
              ? data.migration_phase || instance.migration_phase
              : null,
          phone_number:
            data.phone_number ||
            data.instance?.phone_number ||
            instance.phone_number,
        }
      : instance
  );
};

const accessDeniedMessage = permission =>
  accessProfileMessage({
    permission,
    profileName: currentAccount.value?.custom_role?.name,
  });
const profileAllowsManagement = permission => {
  const scope = permission === 'whatsapp_keys_manage' ? 'keys' : 'inboxes';
  const authority =
    currentAccount.value?.whatsapp_management_authority?.[scope];
  // Trusted server enums distinguish legacy inheritance from explicit revocation.
  // The WhatsApp payload still decides whether inherited access is available.
  if (authority === 'deny') return false;
  if (authority === 'allow' || authority === 'inherit') return true;
  const permissions = currentAccount.value?.permissions || [];
  return (
    !permissions.includes('granular_page_access') ||
    permissions.includes(permission)
  );
};
const leaveBlockedManagementTab = () => {
  if (!['chaves', 'inboxes'].includes(route.query.aba)) return;
  if (isKnownTab(route.query.aba)) {
    activeTab.value = route.query.aba;
    return;
  }
  activeTab.value = 'whatsapp';
  setFeedback(
    accessDeniedMessage(
      route.query.aba === 'chaves'
        ? 'whatsapp_keys_manage'
        : 'managed_inboxes_manage'
    ),
    'error'
  );
  router.replace({ query: { ...route.query, aba: 'whatsapp' } });
};

const applyWhatsappPayload = ({ whatsapp, sellers: sellersList }) => {
  whatsappLoadFailed.value = false;
  whatsappLoadFailureMessage.value = '';
  instances.value = whatsapp.instances || [];
  teamOptions.value = whatsapp.team_options || [];
  wamePool.value = whatsapp.wame_pool || { configured: 0, available: 0 };
  whatsappProviders.value = whatsapp.providers || [];
  viewerIsManager.value =
    Boolean(whatsapp.viewer_is_manager) &&
    profileAllowsManagement('whatsapp_keys_manage');
  viewerCanManageInboxes.value =
    Boolean(whatsapp.viewer_can_manage_inboxes ?? whatsapp.viewer_is_manager) &&
    profileAllowsManagement('managed_inboxes_manage');
  leaveBlockedManagementTab();
  viewerCanCreate.value = Boolean(whatsapp.viewer_can_create);
  viewerIsSupervisor.value = Boolean(whatsapp.viewer_is_supervisor);
  sellers.value = sellersList || [];
};

// `useCache` so na abertura da tela: a guarda de rota ja trouxe o payload, e
// repetir a chamada aqui e o que fazia a pagina abrir em "Carregando
// conexoes...". Depois de qualquer acao o cache e descartado e refeito, para
// nunca mostrar estado velho de conexao.
const loadHub = async ({ useCache = false } = {}) => {
  const accountId = route.params.accountId;
  const cached = useCache ? getIntegracoesPageData(accountId) : null;

  if (cached) {
    applyWhatsappPayload(cached);
    loading.value = false;
    // eslint-disable-next-line no-use-before-define
    await loadAuxiliaryIntegrations();
    return;
  }

  loading.value = true;
  whatsappLoadFailed.value = false;
  whatsappLoadFailureMessage.value = '';
  clearIntegracoesPageData();

  try {
    const payload = await prefetchIntegracoesPageData(accountId);
    if (!payload) {
      const message = getIntegracoesPageFailureMessage(
        getIntegracoesPageFailure(accountId)
      );
      whatsappLoadFailed.value = true;
      whatsappLoadFailureMessage.value = message;
      setFeedback(message, 'error');
    } else {
      applyWhatsappPayload(payload);
    }
  } catch (error) {
    const message = getIntegracoesPageFailureMessage({
      code: error?.code,
      httpStatus: error?.httpStatus,
    });
    whatsappLoadFailed.value = true;
    whatsappLoadFailureMessage.value = message;
    setFeedback(message, 'error');
  } finally {
    loading.value = false;
  }

  // eslint-disable-next-line no-use-before-define
  await loadAuxiliaryIntegrations();
};

// The auth store is updated after the official profile refresh completes.
// Observe the account ACL rather than the event emitted before that API read.
watch(
  () =>
    JSON.stringify([
      currentAccount.value?.id,
      currentAccount.value?.role,
      currentAccount.value?.custom_role_id,
      currentAccount.value?.permissions,
      currentAccount.value?.whatsapp_management_authority,
    ]),
  () => {
    if (!profileAllowsManagement('whatsapp_keys_manage')) {
      viewerIsManager.value = false;
      poolInfoOpen.value = false;
      contractRequestOpen.value = false;
    }
    if (!profileAllowsManagement('managed_inboxes_manage')) {
      viewerCanManageInboxes.value = false;
    }
    leaveBlockedManagementTab();
    clearIntegracoesPageData();
    loadHub();
  }
);

// E-mail, Gmail e o mapa de instâncias saíram deste hub: a central de
// integrações do Zoraxy cobre WhatsApp e canais sociais, e os demais seguem
// no Aceleres. Sobrou uma chamada só — mas `allSettled` continua certo: uma
// falha aqui não pode derrubar a aba de WhatsApp, que é o que importa na tela.
/**
 * Estado dos canais de Instagram e Messenger, que é o que muda sozinho enquanto
 * alguém olha a tela — por isso é o único relido pela varredura periódica.
 */
const loadWameChannels = async () => {
  try {
    const { data } = await wameSocialChannel.list();
    wameChannels.value = data.payload.channels || [];
    wameLoadFailed.value = false;
  } catch (error) {
    // Falhar aqui não pode passar por "nenhum canal conectado": sem o estado
    // real a aba mostraria o vazio de sempre e esconderia canais que existem.
    wameLoadFailed.value = true;
  }
};

const loadLinkedinStatus = async () => {
  try {
    const { data } = await linkedinIntegration.status();
    linkedinState.value = data;
    linkedinLoadFailed.value = false;
  } catch (error) {
    linkedinLoadFailed.value = true;
  }
};

const loadAuxiliaryIntegrations = async () => {
  await Promise.allSettled([
    loadLinkedinStatus(),
    aceleresIntegrations
      .getSocialChannels()
      .then(data => {
        mirroredSocialChannels.value = data.channels || [];
      })
      .catch(() => {}),
    loadWameChannels(),
  ]);
};

const pollWhatsappStatus = async instanceId => {
  if (qrPollingInFlight || !canPollWhatsappStatus(instanceId)) return;

  qrPollingInFlight = true;
  try {
    const data = await aceleresIntegrations.getWhatsappStatus(instanceId);

    // 503 com `retryable` é o provedor oscilando, não uma queda: nem atualiza o
    // card nem descarta o QR — o pareamento costuma concluir mesmo assim.
    if (data.retryable) {
      deferWhatsappStatus(instanceId, data);
      setFeedback(
        data.code === 'PROVIDER_RATE_LIMITED'
          ? 'A verificação da conexão será retomada em instantes.'
          : 'WhatsApp instável no momento — continuamos tentando…'
      );
      return;
    }

    updateInstanceStatus(instanceId, data);

    // Depois que o aparelho inicia o handshake, o QR já foi lido e não deve
    // continuar na tela.
    if (data.pairing_started === true || data.qr_available === false) {
      if (qrConnection.value?.instanceId === instanceId) {
        qrConnection.value = { ...qrConnection.value, pairingStarted: true };
      }
    }

    if (data.status === 'connected' || data.instance?.is_connected) {
      qrConnection.value = null;
      stopQrPolling();
      createWizardRef.value?.close();
      setFeedback('WhatsApp conectado com sucesso.');
      await loadHub();
      await loadKeyState();
    } else if (['disconnected', 'failed'].includes(data.status)) {
      qrConnection.value = null;
      stopQrPolling();
      createWizardRef.value?.close();
      await loadHub();
      await loadKeyState();
    } else if (data.status === 'migration_pending') {
      qrConnection.value = null;
      stopQrPolling();
      createWizardRef.value?.close();
      // eslint-disable-next-line no-use-before-define
      await loadMigration(instanceId);
    }
  } catch (error) {
    // Vaga devolvida ou pool cheio não se resolvem sozinhos: manter o QR na
    // tela e seguir consultando prometia uma tentativa que ninguém está
    // fazendo. O caminho de volta é o botão Conectar.
    if (isKeyUnavailable(error)) {
      qrConnection.value = null;
      stopQrPolling();
      createWizardRef.value?.close();
      setFeedback(
        `${error.message} A conexão continua na lista, desconectada — use o botão Conectar dela quando houver uma conexão livre.`,
        'error'
      );
      // Sem loadHub: o skeleton da recarga apagava o aviso da tela. A lista já
      // reflete o estado, e a varredura periódica de status cuida do resto.
      return;
    }
    // Falhas de polling não alteram a sessão nem descartam um QR válido.
    setFeedback(
      error.message || 'Sem resposta do servidor — continuamos tentando…',
      'error'
    );
  } finally {
    qrPollingInFlight = false;
  }
};

/**
 * Renova o QR enquanto ninguém o leu. O código da API 2 expira em ~40s; sem
 * isso o usuário ficava olhando uma imagem morta até desistir.
 */
const refreshQrCode = async instanceId => {
  if (qrConnection.value?.instanceId !== instanceId) return;
  if (qrConnection.value?.pairingStarted) return;

  try {
    const data = await aceleresIntegrations.getWhatsappQr(instanceId);
    if (data.pairing_started === true) {
      qrConnection.value = { ...qrConnection.value, pairingStarted: true };
      return;
    }
    if (data.qrcode?.base64) {
      qrConnection.value = {
        ...qrConnection.value,
        base64: data.qrcode.base64,
        pairingCode: data.pairing_code || null,
      };
    }
  } catch {
    // Um QR antigo na tela é melhor que nenhum: o polling de status decide.
  }
};

/**
 * Fechar o assistente pelo X é ocultar, não cancelar.
 *
 * Distinção herdada da seção que existia antes: ali "Ocultar QR" e "Cancelar
 * tentativa" eram dois botões, porque quem já leu o código não pode ter a sessão
 * derrubada só por fechar a janela. Sem limpar o estado aqui, `qrConnection`
 * ficaria preso e o polling dos cards — que para justamente enquanto há um
 * pareamento em curso — nunca voltaria.
 */
const onPairingDialogClosed = () => {
  if (!qrConnection.value) return;
  stopQrPolling();
  qrConnection.value = null;
};

// Ações do rodapé da etapa de pareamento. O estado do QR continua morando aqui:
// o assistente desenha, o hub decide.
const refreshPairingCode = () => {
  const instanceId = qrConnection.value?.instanceId;
  if (instanceId) refreshQrCode(instanceId);
};

const startQrPolling = instanceId => {
  stopQrPolling();
  qrPollingTimer = window.setInterval(
    () => pollWhatsappStatus(instanceId),
    3000
  );
  qrRefreshTimer = window.setInterval(
    () => refreshQrCode(instanceId),
    QR_REFRESH_MS
  );
};

/**
 * Mantém os cards da lista alinhados com o estado real do provedor.
 *
 * Sem isto o hub só mostrava a coluna `status` do banco, lida uma vez no
 * carregamento: um card marcado como desconectado por engano permanecia assim
 * até alguém clicar em "Conectar" — que apenas relê o estado remoto.
 */
const refreshLiveStatuses = async () => {
  if (livePollingInFlight || document.hidden) return;
  if (qrConnection.value) return; // o modal de QR tem o próprio polling

  livePollingInFlight = true;
  livePollingTick += 1;
  const includeIdle = livePollingTick % IDLE_POLL_EVERY_TICKS === 0;
  const pollable = includeIdle
    ? ACTIVE_STATUSES.concat(IDLE_STATUSES)
    : ACTIVE_STATUSES;

  try {
    // O filtro era `instance.can_manage`, e isso trocava dois conceitos
    // diferentes: `can_manage` é permissão de ADMINISTRAR a conexão (a porta
    // Rails a rebaixa para inbox compartilhada fora da equipe, em
    // whatsapp_instances_controller.rb:381), não permissão de LER o estado
    // dela. O efeito era um card que nunca se atualizava sozinho: medido em
    // 04/09/2026, com a tela aberta por 90s, as duas conexões `individual_own`
    // receberam 13 consultas de status e a `shared` marcada "Somente leitura"
    // recebeu zero — ela só mudava com F5. Quem vê o card precisa ver o estado
    // certo do card, mesmo sem poder mexer nele.
    const targets = instances.value.filter(
      instance =>
        pollable.includes(instance.status) && canPollWhatsappStatus(instance.id)
    );

    // Sequencial de propósito: cada consulta chega ao provedor remoto e o
    // paralelo transformaria uma lista grande em rajada.
    for (let index = 0; index < targets.length; index += 1) {
      if (qrConnection.value) break;
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await aceleresIntegrations.getWhatsappStatus(
          targets[index].id
        );
        if (data.retryable) {
          deferWhatsappStatus(targets[index].id, data);
        } else {
          updateInstanceStatus(targets[index].id, data);
        }
        if (selectedInstanceId.value === targets[index].id && migration.value) {
          // eslint-disable-next-line no-await-in-loop, no-use-before-define
          await loadMigration(targets[index].id);
        }
      } catch {
        // Uma instância inacessível não interrompe as demais.
      }
    }
  } finally {
    livePollingInFlight = false;
  }
};

const startLivePolling = () => {
  if (livePollingTimer) window.clearInterval(livePollingTimer);
  livePollingTimer = window.setInterval(
    refreshLiveStatuses,
    LIVE_POLL_INTERVAL_MS
  );
};

const stopLivePolling = () => {
  if (livePollingTimer) {
    window.clearInterval(livePollingTimer);
    livePollingTimer = null;
  }
};

/**
 * Mantém a aba de canais sociais em dia sem pedir um clique.
 *
 * Um canal só muda de estado por dois caminhos, e nenhum deles avisa esta tela:
 * a autorização termina no navegador (a WAME não notifica o servidor — ver
 * Wame::InstanceStateService) e o vínculo com a conta se fecha quando o PRIMEIRO
 * evento chega pela ponte de webhooks. Enquanto houver canal nesse meio de
 * caminho, releitura curta; com tudo estável, só ao entrar na aba. Era isso que
 * o botão "Atualizar canais" empurrava para o usuário.
 */
const refreshSocialChannels = async () => {
  if (document.hidden || socialPollingInFlight) return;

  socialPollingInFlight = true;
  try {
    // Só os canais WAME: o espelho do Aceleres não muda sozinho, e relê-lo a
    // cada ciclo dobrava as requisições sem nada a mostrar.
    await loadWameChannels();
  } finally {
    socialPollingInFlight = false;
  }
};

const startSocialPolling = () => {
  if (socialPollingTimer) return;
  socialPollingTimer = window.setInterval(
    refreshSocialChannels,
    SOCIAL_POLL_INTERVAL_MS
  );
};

const stopSocialPolling = () => {
  if (socialPollingTimer) {
    window.clearInterval(socialPollingTimer);
    socialPollingTimer = null;
  }
};

const updateWamePool = payload => {
  if (
    !payload ||
    Number(payload.configured) < 0 ||
    Number(payload.available) < 0
  )
    return;
  wamePool.value = {
    configured: Number(payload.configured),
    available: Number(payload.available),
  };
};

/**
 * Recebe o resultado do assistente. A validação de nome, responsável e API
 * acontece lá dentro, passo a passo — aqui só resta o envio.
 */
const createWhatsapp = async payload => {
  setLoading('create', true);
  try {
    const data = await aceleresIntegrations.createWhatsapp({
      display_name: payload.display_name,
      connection_type: payload.connection_type,
      default_assignee_user_id: payload.default_assignee_user_id,
      provider: 'api2',
      connection_api: payload.connection_api,
    });

    setFeedback(
      data.message || 'Conexão criada. Conclua o pareamento para ativá-la.'
    );
    const createdId = data.inbox?.id;
    // O assistente termina no aviso de versão e o pareamento é a etapa seguinte,
    // dentro do mesmo modal. Antes daqui ele fechava e o QR aparecia numa seção
    // solta no fim da página, abaixo da lista de conexões.
    // A resposta da criação já traz os dados necessários para conectar. Esperar
    // loadHub aqui acrescentava várias leituras da página antes do primeiro POST
    // de pareamento, atrasando o pedido do QR.
    if (createdId) {
      const created = {
        ...data.inbox,
        id: createdId,
        name: data.inbox.display_name || payload.display_name,
        label: data.inbox.display_name || payload.display_name,
        connection_api:
          data.inbox.connection_api || payload.connection_api || 'api2',
        provider: data.inbox.provider || 'api2',
      };

      createWizardRef.value?.beginPairing();
      // eslint-disable-next-line no-use-before-define
      await connectWhatsapp(created);

      // O executor pode reaproveitar um placeholder com o mesmo ID que já está
      // no cache da página. Nessa situação, atualizar por ID não basta: a API
      // devolve nome/tipo novos, mas a linha continuava com o snapshot antigo.
      // Mesclamos a resposta autoritativa da criação e preservamos campos de
      // acesso que só existem no payload da listagem (can_manage/can_connect).
      const syncCreatedInstance = () => {
        const index = instances.value.findIndex(
          instance => String(instance.id) === String(createdId)
        );
        if (index < 0) return false;

        instances.value.splice(index, 1, {
          ...instances.value[index],
          ...data.inbox,
          id: createdId,
          name: created.name,
          label: created.label,
          display_name: created.name,
          connection_type: created.connection_type,
          connection_api: created.connection_api,
          provider: created.provider,
        });
        return true;
      };

      // Conexões novas ainda não estão no snapshot inicial: carrega a listagem
      // e reaplica a resposta da criação caso ela tenha entrado pelo cache em
      // paralelo.
      if (!syncCreatedInstance()) {
        await loadHub();
        await loadKeyState();
        syncCreatedInstance();
      }
      return;
    }

    await loadHub();
    await loadKeyState();
    const created = instances.value.find(
      instance => String(instance.id) === String(createdId)
    );
    if (created) {
      createWizardRef.value?.beginPairing();
      // eslint-disable-next-line no-use-before-define
      await connectWhatsapp(created);
    } else {
      createWizardRef.value?.close();
    }
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading('create', false);
  }
};

const openSettings = async instance => {
  groupSettingsSession += 1;
  selectedInstanceId.value = instance.id;
  ['groups', 'groups-sync', 'groups-save'].forEach(action => {
    setLoading(`${action}-${instance.id}`, false);
  });
  qrConnection.value = null;
  groupConfig.value = null;
  persistedGroupConfig.value = null;
  migration.value = null;
  editForm.display_name = instance.name || '';
  editForm.connection_type =
    normalizeAceleresConnectionType(instance.connection_type) ||
    'individual_own';
  editForm.default_assignee_user_id = instance.default_assignee_user_id || '';
  editForm.connection_api =
    instance.connection_api || instance.provider || 'api2';
  editForm.origin_team_id = instance.origin_team?.id || '';
  editForm.team_ids = [...(instance.team_access_team_ids || [])];
  teamSearch.value = '';
  groupSearch.value = '';

  await nextTick();
  settingsDialog.value?.open();

  // eslint-disable-next-line no-use-before-define
  await loadMigration(instance.id, { refresh: true });
};

const closeSettings = () => {
  groupSettingsSession += 1;
  selectedInstanceId.value = null;
  groupConfig.value = null;
  persistedGroupConfig.value = null;
  migration.value = null;
  teamSearch.value = '';
  groupSearch.value = '';
};

const toggleTeamAccess = teamId => {
  if (isOriginTeam(teamId)) return;

  editForm.team_ids = editForm.team_ids.includes(teamId)
    ? editForm.team_ids.filter(id => id !== teamId)
    : [...editForm.team_ids, teamId];
};

/**
 * Inicia (ou retoma) o pareamento de uma inbox que já existe.
 *
 * `openWizard` é o caminho do botão "Conectar" do card: a inbox está criada, o
 * nome, o tipo e a API já foram decididos, então o assistente abre direto na
 * etapa do QR em vez de repetir o que já está resolvido.
 */
const connectWhatsapp = async (instance, { openWizard = false } = {}) => {
  setLoading(`connect-${instance.id}`, true);
  qrConnection.value = null;
  stopQrPolling();

  if (openWizard) {
    createWizardRef.value?.openForPairing({ name: instance.name });
  }

  qrConnection.value = {
    instanceId: instance.id,
    base64: null,
    pairingCode: null,
    pairingStarted: false,
    status: 'Preparando a conexão…',
  };

  try {
    const data = await aceleresIntegrations.connectWhatsapp(instance.id, {
      connection_api: instance.connection_api || instance.provider || 'api2',
    });

    if (data.qrcode?.base64 || data.pairing_code) {
      qrConnection.value = {
        instanceId: instance.id,
        base64: data.qrcode?.base64 || null,
        pairingCode: data.pairing_code || null,
        pairingStarted: false,
        status: 'Aguardando a leitura do código (válido ~40s).',
      };
    }

    if (data.already_connected || data.status === 'connected') {
      qrConnection.value = null;
      createWizardRef.value?.close();
      setFeedback(
        data.message || 'A conexão já estava ativa e foi reconciliada.'
      );
      await loadHub();
      await loadKeyState();
      return;
    }

    if (data.status === 'migration_pending') {
      qrConnection.value = null;
      createWizardRef.value?.close();
      setFeedback(
        data.message || 'A conexão aguarda a confirmação da consolidação.',
        'error'
      );
      await openSettings(instance);
      return;
    }

    setFeedback(
      data.pairing_code
        ? `Código de pareamento: ${data.pairing_code}`
        : 'Pareamento iniciado. Leia o QR Code no WhatsApp.'
    );
    await loadHub();
    await loadKeyState();
    startQrPolling(instance.id);
  } catch (error) {
    // Sem conexão livre não há o que reconciliar: o pareamento nem começou.
    // Parar aqui preserva o aviso na tela — o polling de 3s o substituía antes
    // de dar tempo de ler — e diz o que acontece com a conexão recém-criada.
    if (isKeyUnavailable(error)) {
      qrConnection.value = null;
      stopQrPolling();
      createWizardRef.value?.close();
      setFeedback(
        `${error.message} A conexão foi criada e continua na lista, desconectada. Assim que uma conexão for liberada, use o botão Conectar dela para gerar o QR Code.`,
        'error'
      );
      return;
    }
    // O provedor pode aceitar o pareamento e responder depois de um timeout.
    // Mantemos o polling para reconciliar sem rebaixar uma sessão válida.
    startQrPolling(instance.id);
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`connect-${instance.id}`, false);
  }
};

/**
 * Aviso da vaga reservada.
 *
 * Desconectar pelo botão devolve a vaga na hora; cair sozinha reserva por sete
 * dias, para o dono reconectar o mesmo número sem perder o lugar. Só o segundo
 * caso chega aqui, e é o único que precisa de uma saída manual.
 *
 * O texto anterior — "Vaga de conexão retida até <data> — reconecte antes disso
 * para mantê-la" — mostrava um timestamp cru e não dizia por que a vaga estava
 * retida nem que dava para abrir mão dela.
 */
const keyReleaseNotice = instance => {
  const deadline = instance?.key_release_at
    ? new Date(instance.key_release_at).getTime()
    : null;

  if (!deadline) {
    return 'Esta conexão ainda está ocupando uma vaga de WhatsApp da empresa.';
  }

  const remaining = deadline - Date.now();
  if (remaining <= 0) {
    return 'O prazo terminou: a vaga será devolvida à empresa nos próximos minutos.';
  }

  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const prazo =
    days > 0
      ? `${days} ${days === 1 ? 'dia' : 'dias'} e ${hours}h`
      : `${hours}h`;

  return `A conexão caiu sozinha. Guardamos sua vaga por mais ${prazo} para você reconectar este número; depois disso ela volta para a empresa.`;
};

const releaseWhatsappKey = async instance => {
  if (!instance) return;

  setLoading(`release-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.releaseWhatsappKey(instance.id);
    setFeedback(data.message || 'Chave devolvida ao cofre de chaves.');
    await loadHub();
    await loadKeyState();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`release-${instance.id}`, false);
  }
};

const requestKeyRelease = async instance => {
  releaseConfirmation.value = instance || null;
  await nextTick();
  keyReleaseDialog.value?.open();
};

const closeKeyReleaseConfirmation = () => {
  releaseConfirmation.value = null;
};

const confirmKeyRelease = async () => {
  const instance = releaseConfirmation.value;
  if (!instance) return;

  await releaseWhatsappKey(instance);
  closeKeyReleaseConfirmation();
};

const disconnectWhatsapp = async instance => {
  if (!instance) return;

  setLoading(`disconnect-${instance.id}`, true);
  stopQrPolling();
  if (qrConnection.value?.instanceId === instance.id) qrConnection.value = null;

  try {
    const data = await aceleresIntegrations.disconnectWhatsapp(instance.id);
    setFeedback(
      data.message || 'Conexão desconectada. A caixa de entrada foi preservada.'
    );
    await loadHub();
    await loadKeyState();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`disconnect-${instance.id}`, false);
  }
};

/**
 * Fecha a tentativa de pareamento sem tratá-la como desconexão.
 *
 * O botão usava `disconnect`, que apaga a sessão remota e devolve a chave ao
 * pool. Quando o usuário já tinha lido o QR e só fechava a janela, isso
 * derrubava uma conexão recém-criada. O backend decide pelo estado remoto: só
 * cancela se ninguém leu o código.
 */
const cancelConnectAttempt = async instanceId => {
  const instance = instances.value.find(item => item.id === instanceId);
  if (!instance) return;

  setLoading(`cancel-${instanceId}`, true);
  stopQrPolling();
  qrConnection.value = null;

  try {
    const data = await aceleresIntegrations.cancelConnectWhatsapp(instanceId);
    setFeedback(
      data.preserved
        ? 'O pareamento já havia começado e foi preservado.'
        : 'Tentativa de conexão cancelada.'
    );
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`cancel-${instanceId}`, false);
    await loadHub();
    await loadKeyState();
  }
};

// Desfaz o "Não mostrar mais" do modal de instâncias desconectadas.
const reactivateDisconnectAlert = async instance => {
  setLoading(`disconnect-alert-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.updateWhatsappDisconnectAlert(
      instance.id,
      'off'
    );
    setFeedback(data.message || 'Aviso de desconexão reativado.');
    await loadHub();
    await loadKeyState();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`disconnect-alert-${instance.id}`, false);
  }
};

const setPrimaryInstance = async instance => {
  setLoading(`primary-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.setWhatsappPrimary(instance.id);
    setFeedback(data.message || 'Instância definida como principal.');
    await loadHub();
    await loadKeyState();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`primary-${instance.id}`, false);
  }
};

/**
 * Remover apaga a inbox e o histórico — diferente de desconectar. Por isso a
 * prévia do backend é exibida antes da confirmação, como no Aceleres.
 */
const openDeletionPreview = async instance => {
  setLoading(`delete-preview-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.getWhatsappDeletionPreview(
      instance.id
    );
    deletionPreview.value = { instance, ...data };
    await nextTick();
    deletionDialog.value?.open();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`delete-preview-${instance.id}`, false);
  }
};

const closeDeletionPreview = () => {
  deletionPreview.value = null;
};

const confirmDeletion = async () => {
  const instance = deletionPreview.value?.instance;
  if (!instance) return;

  setLoading(`delete-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.deleteWhatsapp(instance.id);
    setFeedback(data.message || 'Conexão removida.');
    deletionDialog.value?.close();
    deletionPreview.value = null;
    if (selectedInstanceId.value === instance.id)
      selectedInstanceId.value = null;
    await loadHub();
    await loadKeyState();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`delete-${instance.id}`, false);
  }
};

const saveSettings = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;

  if (
    editForm.connection_type === 'individual_company' &&
    !editForm.default_assignee_user_id
  ) {
    setFeedback('Escolha o responsável pela conexão corporativa.', 'error');
    return;
  }

  const shouldSaveTeamAccess =
    editForm.connection_type === 'shared' && instance.can_manage_team_access;
  const originTeamId =
    instance.origin_team?.id || Number(editForm.origin_team_id) || null;
  if (shouldSaveTeamAccess && !originTeamId) {
    setFeedback(
      originEligibleTeamOptions.value.length
        ? 'Selecione uma equipe em que você participa para definir a origem da conexão.'
        : 'O gestor original não participa de nenhuma equipe desta conta. Inclua-o em uma equipe para definir a origem da conexão.',
      'error'
    );
    return;
  }
  if (
    shouldSaveTeamAccess &&
    !instance.origin_team &&
    !originEligibleTeamOptions.value.some(team => team.id === originTeamId)
  ) {
    setFeedback(
      'A equipe escolhida não pode ser a origem. Selecione uma equipe da qual você participa.',
      'error'
    );
    return;
  }

  setLoading(`settings-${instance.id}`, true);
  try {
    if (shouldSaveTeamAccess) {
      await aceleresIntegrations.updateWhatsappTeamAccess(instance.id, {
        team_ids: [...new Set([...editForm.team_ids, originTeamId])],
      });
    }
    await aceleresIntegrations.updateWhatsappType(instance.id, {
      connection_type: editForm.connection_type,
      default_assignee_user_id:
        editForm.connection_type === 'individual_company'
          ? Number(editForm.default_assignee_user_id)
          : null,
      connection_api: editForm.connection_api,
    });
    await aceleresIntegrations.renameWhatsapp(instance.id, {
      display_name: editForm.display_name.trim(),
    });
    // A seleção de grupos vive no MESMO modal e tinha botão próprio: quem
    // desmarcava e clicava no botão grande do rodapé — o maior, o último, o
    // que a posição promete — via as caixas desmarcadas na tela e saía
    // achando que tinha salvo. Nada era enviado, e ao reabrir tudo voltava.
    // Agora o rodapé salva as duas coisas; a confirmação de remoção já foi
    // dada antes de chegar aqui (ver requestSaveSettings).
    // eslint-disable-next-line no-use-before-define
    if (hasPendingGroupChanges.value) {
      // eslint-disable-next-line no-use-before-define
      await saveGroups({ confirmado: true, silencioso: true });
    }
    setFeedback(
      'Configurações salvas. A caixa de entrada e o histórico foram preservados.'
    );
    await loadHub();
    await loadKeyState();
    settingsDialog.value?.close();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`settings-${instance.id}`, false);
  }
};

const loadGroups = async (
  id,
  {
    preserveConfig = Boolean(groupConfig.value),
    revision = groupEditRevision,
    session = groupSettingsSession,
  } = {}
) => {
  if (session !== groupSettingsSession || id !== selectedInstanceId.value)
    return;
  const request = ++groupRequestSequence;
  setLoading(`groups-${id}`, true);
  try {
    const data = await aceleresIntegrations.getWhatsappGroups(id);
    if (
      session !== groupSettingsSession ||
      id !== selectedInstanceId.value ||
      request !== groupRequestSequence
    ) {
      return;
    }
    // Refresh the inventory without discarding an unsaved choice. A save may
    // adopt the persisted snapshot only if no edit happened since it started.
    const config = groupConfig.value?.config;
    // O que veio do servidor e a referencia do que esta salvo, mesmo quando a
    // escolha em edicao e preservada logo abaixo.
    persistedGroupConfig.value = data.config
      ? JSON.parse(JSON.stringify(data.config))
      : null;
    groupConfig.value = {
      ...data,
      config:
        config && (preserveConfig || revision !== groupEditRevision)
          ? config
          : data.config,
    };
    return data;
  } catch (error) {
    if (session === groupSettingsSession && request === groupRequestSequence) {
      setFeedback(error.message, 'error');
    }
  } finally {
    if (request === groupRequestSequence) setLoading(`groups-${id}`, false);
  }
};

const syncGroups = async id => {
  const session = groupSettingsSession;
  setLoading(`groups-sync-${id}`, true);
  try {
    const data = await aceleresIntegrations.syncWhatsappGroups(id);
    if (session !== groupSettingsSession) return;
    const refreshed = await loadGroups(id, { session });
    if (refreshed && session === groupSettingsSession) {
      const warning = data.warning || refreshed.warning;
      const unavailable =
        data.sync_status === 'unavailable' ||
        refreshed.sync_status === 'unavailable';
      const hasGroups = Boolean(refreshed.available_groups?.length);
      setFeedback(
        unavailable
          ? warning || 'Não foi possível atualizar os grupos. Tente novamente.'
          : warning
            ? 'Não encontrou algum grupo? Sincronize novamente em instantes.'
            : data.message || 'Grupos sincronizados.',
        unavailable
          ? 'error'
          : warning
            ? hasGroups
              ? 'groups-updated'
              : 'groups-notice'
            : 'success'
      );
    }
  } catch (error) {
    if (session === groupSettingsSession) setFeedback(error.message, 'error');
  } finally {
    if (session === groupSettingsSession)
      setLoading(`groups-sync-${id}`, false);
  }
};

const toggleGroup = jid => {
  groupEditRevision += 1;
  const allowed = groupConfig.value?.config?.allowed_jids || [];
  groupConfig.value.config.allowed_jids = allowed.includes(jid)
    ? allowed.filter(item => item !== jid)
    : [...allowed, jid];
};

// Grupos que sairiam da selecao se o estado atual fosse salvo agora.
//
// Salvar uma remocao tira o grupo do atendimento: o Aceleres ENCERRA no Chatwoot
// as conversas daquele grupo naquela inbox (purgeGroupConversations -> ponte
// interna). Desde 14/09/2026 nada e apagado (manual 7.4); o historico fica
// consultavel. Ainda assim a conversa sai das abas, e por isso a
// tela precisa saber, ANTES de salvar, exatamente o que esta prestes a sumir.
const groupsBeingRemoved = computed(() => {
  const saved = persistedGroupConfig.value;
  const current = groupConfig.value?.config;
  if (!saved || !current) return [];

  // Desligar "Receber grupos" remove todos de uma vez.
  const before = saved.groups_enabled ? saved.allowed_jids || [] : [];
  const after = current.groups_enabled ? current.allowed_jids || [] : [];
  const removidos = before.filter(jid => !after.includes(jid));

  const catalogo = groupConfig.value?.available_groups || [];
  return removidos.map(jid => {
    const grupo = catalogo.find(item => item.group_jid === jid);
    return { jid, subject: grupo?.subject || jid };
  });
});

const hasPendingGroupChanges = computed(() => {
  const saved = persistedGroupConfig.value;
  const current = groupConfig.value?.config;
  if (!saved || !current) return false;

  const normaliza = config => ({
    enabled: Boolean(config.groups_enabled),
    mode: config.groups_mode,
    jids: [...(config.allowed_jids || [])].sort(),
  });

  return (
    JSON.stringify(normaliza(saved)) !== JSON.stringify(normaliza(current))
  );
});

const describeGroupRemoval = () => {
  const removidos = groupsBeingRemoved.value;
  const nomes = removidos.map(item => `“${item.subject}”`).join(', ');
  const plural = removidos.length > 1;

  return (
    `${plural ? 'Os grupos' : 'O grupo'} ${nomes} ${plural ? 'saem' : 'sai'} do atendimento, e ` +
    `${plural ? 'as conversas deles são encerradas' : 'a conversa dele é encerrada'}. ` +
    'O histórico de mensagens é preservado e continua disponível para consulta. ' +
    `As mensagens que chegarem ${plural ? 'desses grupos' : 'desse grupo'} ` +
    'deixam de entrar.'
  );
};

// Ponto unico de saida da selecao de grupos. `confirmado` so vem true depois
// de a pessoa ler o que vai sair do atendimento no dialogo de confirmacao.
const saveGroups = async ({ confirmado = false, silencioso = false } = {}) => {
  if (!confirmado && groupsBeingRemoved.value.length) {
    // eslint-disable-next-line no-use-before-define
    openActionConfirmation({
      kind: 'groups-remove',
      title:
        groupsBeingRemoved.value.length > 1
          ? `Remover ${groupsBeingRemoved.value.length} grupos do atendimento?`
          : 'Remover este grupo do atendimento?',
      description: describeGroupRemoval(),
      confirmLabel: 'Remover e salvar',
      loadingKey: `groups-save-${selectedInstance.value?.id}`,
    });
    return;
  }

  const instance = selectedInstance.value;
  if (
    !instance ||
    !groupConfig.value ||
    actionLoading[`groups-save-${instance.id}`]
  )
    return;
  const session = groupSettingsSession;
  const revision = groupEditRevision;

  setLoading(`groups-save-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.updateWhatsappGroups(instance.id, {
      groups_enabled: Boolean(groupConfig.value.config.groups_enabled),
      groups_mode: groupConfig.value.config.groups_mode,
      allowed_jids: [...(groupConfig.value.config.allowed_jids || [])],
    });
    // O PATCH devolve só `{success, message}` — sem `config` nem
    // `available_groups`. Guardar a resposta em `groupConfig` deixava o painel
    // sem `config` e a tela quebrava logo após salvar. Quem diz o que ficou
    // salvo é o GET, e ele não recarrega a página: só este painel.
    if (session !== groupSettingsSession) return;
    const refreshed = await loadGroups(instance.id, {
      preserveConfig: false,
      revision,
      session,
    });
    if (refreshed && session === groupSettingsSession && !silencioso) {
      setFeedback(data.message || 'Configuração de grupos salva.');
    }
  } catch (error) {
    if (session === groupSettingsSession) setFeedback(error.message, 'error');
  } finally {
    if (session === groupSettingsSession) {
      setLoading(`groups-save-${instance.id}`, false);
    }
  }
};

// Gatilho do botão "Salvar conexão" (rodapé do modal).
//
// Ele salva identidade e grupos de uma vez, então a confirmação da remoção
// precisa vir ANTES de qualquer escrita: confirmar depois de já ter gravado o
// nome deixaria a tela pela metade se a pessoa voltasse atrás.
const requestSaveSettings = () => {
  if (hasPendingGroupChanges.value && groupsBeingRemoved.value.length) {
    // eslint-disable-next-line no-use-before-define
    openActionConfirmation({
      kind: 'settings-save',
      title:
        groupsBeingRemoved.value.length > 1
          ? `Salvar e remover ${groupsBeingRemoved.value.length} grupos do atendimento?`
          : 'Salvar e remover este grupo do atendimento?',
      description: describeGroupRemoval(),
      confirmLabel: 'Salvar e remover',
      loadingKey: `settings-${selectedInstance.value?.id}`,
    });
    return;
  }

  saveSettings();
};

// Fechar o modal (botão "Fechar", Esc ou clique fora) descarta a seleção de
// grupos em edição sem aviso nenhum — e o Dialog não deixa cancelar o próprio
// fechamento. Então o modal fecha, o estado é PRESERVADO, e a pergunta vem em
// seguida: descartar de vez, ou reabrir de onde parou.
const requestCloseSettings = () => {
  if (hasPendingGroupChanges.value) {
    // eslint-disable-next-line no-use-before-define
    openActionConfirmation({
      kind: 'groups-discard',
      title: 'Descartar a seleção de grupos?',
      description:
        'Você mudou quais grupos entram no atendimento e ainda não salvou. ' +
        'Fechar agora descarta essa mudança — nada é apagado e nada é ' +
        'liberado; a conexão continua exatamente como está hoje.',
      confirmLabel: 'Descartar',
      loadingKey: 'groups-discard',
    });
    return;
  }

  closeSettings();
};

const loadMigration = async (id, { refresh = false } = {}) => {
  const session = groupSettingsSession;
  migrationRequestSequence += 1;
  const requestSequence = migrationRequestSequence;
  try {
    const data = await aceleresIntegrations.getWhatsappMigration(id, {
      refresh,
    });
    if (
      session !== groupSettingsSession ||
      id !== selectedInstanceId.value ||
      requestSequence !== migrationRequestSequence
    ) {
      return;
    }
    migration.value = data.migration || null;
  } catch (error) {
    if (
      session !== groupSettingsSession ||
      id !== selectedInstanceId.value ||
      requestSequence !== migrationRequestSequence
    ) {
      return;
    }
    // Abrir o modal de configuração NÃO pode cuspir erro na tela: esta é uma
    // leitura de pano de fundo, e a conexão sem consolidação pendente — que é
    // a esmagadora maioria — não tem nada a relatar. O executor passou a
    // responder 200 com `migration: null` (09/09/2026); o catch aqui existe
    // para a janela de deploy em que o Zoraxy já subiu e o Aceleres ainda
    // responde 404, e para falha de rede.
    //
    // Peneirar por frase, como se fazia até aqui, deixava passar qualquer
    // OUTRO 404 do mesmo endpoint — "Inbox não encontrada." era o caso real —
    // e o usuário era recebido com "Não foi possível concluir esta operação de
    // integração." só por ter aberto o modal. O que importa para quem abre a
    // tela é que não há consolidação a revisar; o erro fica no console.
    migration.value = null;
    // eslint-disable-next-line no-console
    console.warn('[integracoes] prévia de consolidação indisponível', error);
  }
};

// O espelho do Aceleres continua sendo relido, mas deixou de depender de alguém
// clicar: a aba se atualiza sozinha (ver refreshSocialChannels e o polling ao
// abrir a aba). O botão que existia aqui virou "Conectar canal".
const syncSocialChannels = async ({ silent = true } = {}) => {
  try {
    const data = await aceleresIntegrations.syncSocialChannels();
    mirroredSocialChannels.value =
      data.channels || mirroredSocialChannels.value;
    if (!silent) setFeedback(data.message || 'Canais sincronizados.');
  } catch (error) {
    if (!silent) setFeedback(error.message, 'error');
  }
};

// A autorização acontece numa página hospedada pela WAME e sai do Zoraxy por
// redirect de página inteira (a Meta não permite iframe). O `return_url` traz a
// pessoa de volta exatamente para esta aba.
const socialReturnUrl = () => window.location.href.split('?')[0];

// A primeira conexão exige escolher QUAL conexão WAME carrega o canal (a chave é
// recurso escasso e uma chave só aceita um canal de cada tipo), então ela mora
// na tela dedicada, que sabe apresentar essa escolha. O que estava errado não
// era o destino — era o rótulo, que dizia "Ver inboxes nativas do Chatwoot"
// para um botão que abre justamente o fluxo WAME.

// Estado da autorização embutida (Instagram e Messenger).
const wameAuthUrl = ref('');

/**
 * Abre o assistente de conexão social.
 *
 * `provider` vem preenchido quando o clique partiu do card de uma rede; sem ele
 * o assistente começa perguntando qual rede conectar.
 */
const openSocialWizard = (provider = '') => {
  socialWizardError.value = '';
  wameAuthUrl.value = '';
  socialWizardInboxId.value = null;
  socialWizardRef.value?.open({ provider });
};

/**
 * Provisiona o canal e traz a autorização para dentro do assistente.
 *
 * Antes disto o botão do card levava para uma tela avulsa (WameSocialChannel),
 * que repetia a escolha de conexão e terminava num botão solto.
 */
const connectSocialChannel = async ({ provider, name, teamId }) => {
  setLoading('social-connect', true);
  socialWizardError.value = '';
  try {
    const { data } = await wameSocialChannel.connect({
      provider,
      name,
      teamId,
      returnUrl: socialReturnUrl(),
    });

    // A inbox já existe a partir daqui: a lista em memória precisa saber disso
    // para não mostrar a conta como ausente quando o OAuth voltar.
    store.dispatch('inboxes/get');

    socialWizardInboxId.value = data.payload.inbox_id;
    wameAuthUrl.value = data.payload.authorization_url;
    socialWizardRef.value?.showAuthorization();
    await loadAuxiliaryIntegrations();
  } catch (error) {
    socialWizardError.value =
      error?.response?.data?.error ||
      'Não foi possível preparar a conexão. Tente novamente.';
  } finally {
    setLoading('social-connect', false);
  }
};

/**
 * "Já autorizei, verificar" — e também o que roda sozinho quando o iframe avisa.
 *
 * A WAME não notifica o servidor no fim do OAuth (só `GET /{key}/instance`
 * conta a verdade), então esta é a consulta que fecha o ciclo sob demanda.
 */
const verifySocialWizardChannel = async () => {
  const inboxId = socialWizardInboxId.value;
  if (!inboxId) {
    socialWizardError.value = 'Conclua a autorização na janela acima.';
    return;
  }

  setLoading('social-connect', true);
  socialWizardError.value = '';
  try {
    const { data } = await wameSocialChannel.sync(inboxId);
    if (data.payload.status === 'pending') {
      socialWizardError.value =
        'A Meta ainda não confirmou a autorização. Conclua na janela acima e tente de novo.';
    } else {
      socialWizardRef.value?.close();
      setFeedback('Canal autorizado.', 'success');
    }
    await loadAuxiliaryIntegrations();
  } catch (error) {
    socialWizardError.value = 'Não foi possível verificar o canal agora.';
  } finally {
    setLoading('social-connect', false);
  }
};

// Retomar ou refazer a autorização de um canal que já existe.
const authorizeWameChannel = async card => {
  const key = `wame-auth-${card.wame.inbox_id}`;
  setLoading(key, true);
  try {
    const { data } = await wameSocialChannel.reauthorize(
      card.wame.inbox_id,
      socialReturnUrl()
    );
    // Sem o registro do formato de webhook na instância, a autorização não tem
    // a que se vincular: a pessoa conclui tudo na Meta, vê a conta autorizada lá
    // e o canal nunca recebe. Avisar antes é melhor do que deixar autorizar em
    // falso — já custou duas autorizações perdidas em homologação.
    if (data.payload.webhook_registered === false) {
      setFeedback(
        'Não foi possível preparar a Conexão Zoraxy. Autorizar agora não faria o canal receber — tente de novo em instantes ou avise o suporte.',
        'error'
      );
      setLoading(key, false);
      return;
    }
    // A autorização abre aqui dentro, não noutra página. Ver
    // WameAuthorizationFrame: a página da WAME aceita ser embutida e avisa por
    // postMessage quando termina.
    //
    // É o MESMO assistente da primeira conexão, aberto na etapa de autorização:
    // manter um diálogo só para refazer criava duas telas para o mesmo passo, e
    // só uma delas sabia dizer o que aconteceu depois.
    wameAuthUrl.value = data.payload.authorization_url;
    socialWizardInboxId.value = card.wame.inbox_id;
    socialWizardError.value = '';
    socialWizardRef.value?.openAtAuthorization({
      provider: card.wame.provider,
    });
    setLoading(key, false);
  } catch (error) {
    setFeedback(
      'Não foi possível iniciar a autorização. Tente novamente.',
      'error'
    );
    setLoading(key, false);
  }
};

const useExistingSocialInbox = inboxId => {
  const card = activeSocialSection.value.cards.find(
    item => item.wame?.inbox_id === inboxId
  );
  if (!card) {
    socialWizardError.value = 'Esta inbox não está mais disponível.';
    return;
  }

  socialWizardRef.value?.close();
  authorizeWameChannel(card);
};

// A WAME não avisa quando o OAuth termina — só relendo o estado da instância se
// descobre. É o mesmo que a varredura de 5 em 5 minutos faz, sob demanda.
const verifyWameChannel = async card => {
  const key = `wame-sync-${card.wame.inbox_id}`;
  setLoading(key, true);
  try {
    const { data } = await wameSocialChannel.sync(card.wame.inbox_id);
    const status = data.payload.status;
    if (status === 'connected') {
      setFeedback('Canal confirmado: já está recebendo mensagens.');
    } else if (status === 'awaiting') {
      setFeedback(
        'Autorização confirmada na Meta. A conta é identificada na primeira mensagem recebida.'
      );
    } else if (status === 'pending') {
      setFeedback(
        'A Meta ainda não confirmou a autorização. Conclua a autorização antes de verificar novamente.',
        'error'
      );
    } else {
      setFeedback('Este canal não está autorizado na Meta.', 'error');
    }
    await loadAuxiliaryIntegrations();
  } catch (error) {
    setFeedback('Não foi possível verificar o canal agora.', 'error');
  } finally {
    setLoading(key, false);
  }
};

const disconnectWameChannel = async card => {
  const key = `wame-disconnect-${card.wame.inbox_id}`;
  setLoading(key, true);
  try {
    await wameSocialChannel.disconnect(card.wame.inbox_id);
    setFeedback(
      'Canal desconectado. A caixa de entrada e o histórico foram preservados.'
    );
    store.dispatch('inboxes/get');
    await loadAuxiliaryIntegrations();
  } catch (error) {
    setFeedback('Não foi possível desconectar o canal.', 'error');
  } finally {
    setLoading(key, false);
  }
};

const socialRemovalCodeMatches = computed(
  () =>
    socialRemovalPreview.value &&
    socialRemovalCode.value === socialRemovalPreview.value.confirmation_code
);

const openWameRemovalPreview = async card => {
  const key = `wame-remove-preview-${card.wame.inbox_id}`;
  setLoading(key, true);
  try {
    const { data } = await wameSocialChannel.removalPreview(card.wame.inbox_id);
    socialRemovalPreview.value = { card, ...data.payload };
    socialRemovalCode.value = '';
    await nextTick();
    socialRemovalDialog.value?.open();
  } catch (error) {
    setFeedback(
      error?.response?.data?.error ||
        'Não foi possível preparar a remoção do canal.',
      'error'
    );
  } finally {
    setLoading(key, false);
  }
};

const closeWameRemovalPreview = () => {
  socialRemovalPreview.value = null;
  socialRemovalCode.value = '';
};

const removeWameChannel = async () => {
  const preview = socialRemovalPreview.value;
  if (!preview || !socialRemovalCodeMatches.value) return;

  const key = `wame-remove-${preview.inbox_id}`;
  setLoading(key, true);
  try {
    await wameSocialChannel.remove(preview.inbox_id, socialRemovalCode.value);
    socialRemovalDialog.value?.close();
    closeWameRemovalPreview();
    setFeedback(
      'Canal removido. A caixa de entrada, as conversas e o histórico foram arquivados.'
    );
    store.dispatch('inboxes/get');
    await loadAuxiliaryIntegrations();
  } catch (error) {
    setFeedback(
      error?.response?.data?.error || 'Não foi possível remover o canal.',
      'error'
    );
  } finally {
    setLoading(key, false);
  }
};

// O LinkedIn não aceita ser aberto dentro de iframe: a autorização sai do Zoraxy
// por página inteira e volta para esta aba com `?linkedin=<resultado>`.
const connectLinkedin = async () => {
  setLoading('linkedin-connect', true);
  try {
    const { data } = await linkedinIntegration.authorize();
    window.location.assign(data.authorization_url);
  } catch (error) {
    setFeedback(
      error?.response?.data?.message ||
        'Não foi possível iniciar a conexão com o LinkedIn. Tente novamente.',
      'error'
    );
    setLoading('linkedin-connect', false);
  }
};

const disconnectLinkedin = async () => {
  setLoading('linkedin-disconnect', true);
  try {
    const { data } = await linkedinIntegration.disconnect();
    linkedinState.value = { ...linkedinState.value, ...data };
    setFeedback(
      'LinkedIn desconectado. Os leads já recebidos continuam no funil.'
    );
    await loadLinkedinStatus();
  } catch (error) {
    setFeedback(
      error?.response?.data?.message ||
        'Não foi possível desconectar o LinkedIn.',
      'error'
    );
  } finally {
    setLoading('linkedin-disconnect', false);
  }
};

const confirmMigration = async () => {
  const instance = selectedInstance.value;
  if (
    !instance ||
    !migration.value ||
    migration.value.target_instance_id !== instance.id
  )
    return;

  setLoading(`migration-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.confirmWhatsappMigration(
      instance.id,
      {
        accept_single_instance: true,
        accept_api2_exclusive: migration.value.target_provider === 'api2',
      }
    );
    // Confirmar coloca a consolidação na fila; manter o modal aberto fazia a
    // lista atualizar por trás e parecia que a ação não tinha terminado.
    closeSettings();
    settingsDialog.value?.close();
    await loadHub();
    await loadKeyState();
    setFeedback(
      data.message ||
        'Consolidação colocada na fila. O andamento ficará visível no card da conexão.'
    );
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`migration-${instance.id}`, false);
  }
};

const cancelMigration = async () => {
  const instance = selectedInstance.value;
  if (
    !instance ||
    !migration.value ||
    migration.value.target_instance_id !== instance.id
  )
    return;

  setLoading(`migration-cancel-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.cancelWhatsappMigration(
      instance.id,
      {
        accept_cancel: true,
      }
    );
    migration.value = data.migration || migration.value;
    setFeedback(data.message || 'Consolidação cancelada.');
    await loadHub();
    await loadKeyState();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`migration-cancel-${instance.id}`, false);
  }
};

const openActionConfirmation = async confirmation => {
  actionConfirmation.value = confirmation;
  await nextTick();
  actionConfirmationDialog.value?.open();
};

let resolveRouteDiscard = null;
const closeActionConfirmation = () => {
  if (actionConfirmation.value?.kind === 'route-discard') {
    resolveRouteDiscard?.(false);
    resolveRouteDiscard = null;
  }
  // "Voltar" num descarte significa continuar editando: o modal foi fechado
  // para dar lugar a esta pergunta, e o estado ainda esta intacto.
  const voltandoParaEdicao =
    actionConfirmation.value?.kind === 'groups-discard' &&
    selectedInstanceId.value;
  actionConfirmation.value = null;
  if (voltandoParaEdicao) {
    nextTick(() => settingsDialog.value?.open());
  }
};

const requestWhatsappDisconnect = instance =>
  openActionConfirmation({
    kind: 'whatsapp-disconnect',
    subject: instance,
    title: `Desconectar “${instance.name}”?`,
    description:
      'A sessão será encerrada, mas a caixa de entrada e todo o histórico serão preservados.',
    confirmLabel: 'Desconectar',
    loadingKey: `disconnect-${instance.id}`,
  });

// Menu de acoes do cartao: um aberto de cada vez, identificado pela instancia.
const openCardMenuId = ref(null);

const toggleCardMenu = instance => {
  openCardMenuId.value =
    openCardMenuId.value === instance.id ? null : instance.id;
};

// Cada cartao registra o seu proprio listener de clickaway no document. Sem o
// escopo por instancia, o clique que abre o menu de um cartao chega tambem ao
// listener de TODOS os outros — e cada um deles zerava o estado partilhado no
// mesmo clique, deixando o menu impossivel de abrir com mais de um cartao na
// tela. Comprovado em 30/08/2026: com um clique que nao borbulha ate o
// document (sem alcançar os outros listeners) o menu abria normalmente.
const closeCardMenu = instance => {
  if (!instance || openCardMenuId.value === instance.id) {
    openCardMenuId.value = null;
  }
};

const cardMenuItems = instance => {
  const items = [];

  if (instance.status === 'connected') {
    items.push({
      label: 'Desconectar',
      value: 'disconnect',
      action: 'disconnect',
      icon: 'i-lucide-unplug',
    });
  }

  if (instance.status === 'connected' && !instance.is_primary) {
    items.push({
      label: 'Tornar principal',
      value: 'primary',
      action: 'primary',
      icon: 'i-lucide-star',
    });
  }

  if (instance.can_release_key) {
    items.push({
      label: 'Devolver chave ao cofre',
      value: 'release',
      action: 'release',
      icon: 'i-lucide-undo-2',
    });
  }

  items.push({
    label: 'Configurar',
    value: 'settings',
    action: 'settings',
    icon: 'i-lucide-settings-2',
  });

  items.push({
    label: 'Remover',
    value: 'remove',
    action: 'remove',
    icon: 'i-lucide-trash-2',
  });

  return items;
};

const handleCardMenuAction = ({ action }, instance) => {
  closeCardMenu();

  if (action === 'disconnect') requestWhatsappDisconnect(instance);
  else if (action === 'primary') setPrimaryInstance(instance);
  else if (action === 'release') requestKeyRelease(instance);
  else if (action === 'settings') openSettings(instance);
  else if (action === 'remove') openDeletionPreview(instance);
};

const requestWameDisconnect = card =>
  openActionConfirmation({
    kind: 'wame-disconnect',
    subject: card,
    title: `Desconectar “${card.title}”?`,
    // Mesma promessa do WhatsApp, logo acima: desconectar é reversível e não
    // custa histórico. Reconectar a MESMA conexão volta para esta caixa de
    // entrada, porque o canal é reaproveitado pela chave.
    description:
      'O canal será desligado no Zoraxy e deixará de enviar ou receber por esta conexão. A caixa de entrada e todo o histórico serão preservados. Para remover o acesso também na Meta, faça isso nas configurações da conta Meta.',
    confirmLabel: 'Desconectar',
    loadingKey: `wame-disconnect-${card.wame.inbox_id}`,
  });

const socialCardMenuKey = card => `social-${card.wame.inbox_id}`;

const toggleSocialCardMenu = card => {
  const key = socialCardMenuKey(card);
  openCardMenuId.value = openCardMenuId.value === key ? null : key;
};

const closeSocialCardMenu = card => {
  if (!card || openCardMenuId.value === socialCardMenuKey(card)) {
    openCardMenuId.value = null;
  }
};

const socialCardMenuItems = card => {
  const items = [
    {
      label: 'Configurar caixa de entrada',
      value: 'settings',
      action: 'settings',
      icon: 'i-lucide-settings-2',
    },
  ];

  if (card.wame.provider === 'instagram' && canManageWameChannelActions.value) {
    if (card.wame.identity_probe_available) {
      items.push({
        label: 'Verificar identidade automaticamente',
        value: 'verify-identity',
        action: 'identity',
        icon: 'i-lucide-badge-check',
      });
    }
    items.push({
      label: card.wame.account_username
        ? 'Corrigir conta identificada'
        : 'Identificar conta',
      value: 'identity',
      action: 'identity',
      icon: 'i-ri-instagram-line',
    });
  }

  if (
    canManageWameChannelActions.value &&
    ['connected', 'awaiting'].includes(card.wame.status)
  ) {
    items.push({
      label: 'Desconectar',
      value: 'disconnect',
      action: 'delete',
      icon: 'i-lucide-unplug',
    });
  }

  if (canManageWameChannelActions.value) {
    items.push({
      label: 'Remover',
      value: 'remove',
      action: 'delete',
      icon: 'i-lucide-trash-2',
    });
  }

  return items;
};

const handleSocialCardMenuAction = ({ value }, card) => {
  closeSocialCardMenu();

  if (value === 'settings') {
    router.push(
      accountScopedRoute('settings_inbox_show', {
        inboxId: card.wame.inbox_id,
      })
    );
  } else if (value === 'identity') {
    openInstagramIdentity(card);
  } else if (value === 'verify-identity') {
    openActionConfirmation({
      kind: 'instagram-identity-probe',
      subject: card,
      title: `Verificar a identidade de “${card.title}”?`,
      description:
        'O Zoraxy enviará uma única mensagem explicativa para a conta técnica Zoraxy - Sistema. Nenhuma mensagem será enviada a clientes.',
      confirmLabel: 'Enviar verificação',
      loadingKey: `instagram-verify-${card.wame.inbox_id}`,
    });
  } else if (value === 'disconnect') {
    requestWameDisconnect(card);
  } else if (value === 'remove') {
    openWameRemovalPreview(card);
  }
};

const requestLinkedinDisconnect = card =>
  openActionConfirmation({
    kind: 'linkedin-disconnect',
    subject: card,
    title: `Desconectar “${card.title}”?`,
    description:
      'A Company Page deixa de estar vinculada à empresa e os leads dos formulários do LinkedIn param de chegar ao funil. Os leads já recebidos continuam no funil. Para revogar o acesso também no LinkedIn, faça isso nas configurações da sua conta LinkedIn.',
    confirmLabel: 'Desconectar',
    loadingKey: 'linkedin-disconnect',
  });

const requestCancelMigration = () => {
  const instance = selectedInstance.value;
  if (!instance) return;

  openActionConfirmation({
    kind: 'migration-cancel',
    subject: instance,
    title: 'Cancelar consolidação?',
    description: 'A sessão e o histórico desta conexão serão preservados.',
    confirmLabel: 'Cancelar consolidação',
    loadingKey: `migration-cancel-${instance.id}`,
  });
};

const confirmAction = async () => {
  const confirmation = actionConfirmation.value;
  if (!confirmation) return;

  if (confirmation.kind === 'route-discard') {
    resolveRouteDiscard?.(true);
    resolveRouteDiscard = null;
  } else if (confirmation.kind === 'whatsapp-disconnect') {
    await disconnectWhatsapp(confirmation.subject);
  } else if (confirmation.kind === 'wame-disconnect') {
    await disconnectWameChannel(confirmation.subject);
  } else if (confirmation.kind === 'instagram-identity-probe') {
    await verifyInstagramIdentity(confirmation.subject);
  } else if (confirmation.kind === 'linkedin-disconnect') {
    await disconnectLinkedin();
  } else if (confirmation.kind === 'migration-cancel') {
    await cancelMigration();
  } else if (confirmation.kind === 'groups-remove') {
    await saveGroups({ confirmado: true });
  } else if (confirmation.kind === 'settings-save') {
    await saveSettings();
  } else if (confirmation.kind === 'groups-discard') {
    closeSettings();
  }

  actionConfirmationDialog.value?.close();
  actionConfirmation.value = null;
};

// Retorno da página de autorização da WAME.
//
// Ela devolve `?instagram=connected` ou `?instagram=error&message=<texto>` (idem
// `messenger`) — contrato lido do código-fonte da página deles. Como o retorno
// não recarrega a aba, sem isto a pessoa caía na aba de WhatsApp sem nenhuma
// explicação, mesmo quando a WAME tinha dito exatamente o que houve (por
// exemplo, que o plano não cobre o canal).
const waitForSocialConfirmation = delay =>
  new Promise(resolve => {
    window.setTimeout(resolve, delay);
  });

const confirmReturnedSocialChannel = async inboxId => {
  const delays = [0, 1500, 3000];
  const status = await delays.reduce(async (previousAttempt, delay) => {
    const previousStatus = await previousAttempt;
    if (['awaiting', 'connected'].includes(previousStatus)) {
      return previousStatus;
    }
    if (delay) await waitForSocialConfirmation(delay);
    const { data } = await wameSocialChannel.sync(inboxId);
    return data.payload.status;
  }, Promise.resolve('pending'));

  return ['awaiting', 'connected'].includes(status) ? status : 'pending';
};

const readSocialAuthResult = async () => {
  const params = new URLSearchParams(window.location.search);

  const entry = Object.values(WAME_PROVIDER_BY_KEY)
    .map(provider => ({ provider, outcome: params.get(provider) }))
    .find(item => item.outcome);

  if (!entry) return;

  activeTab.value = 'social';
  // Limpa antes da chamada remota: F5, timeout ou 502 não podem repetir uma
  // confirmação e abrir múltiplas sincronizações em paralelo.
  router.replace({ query: { aba: 'social' } });

  if (entry.outcome === 'error') {
    const inboxId = params.get('wame_inbox_id');
    if (/^\d+$/.test(inboxId || '')) {
      try {
        await wameSocialChannel.discard(inboxId);
        await loadAuxiliaryIntegrations();
      } catch (error) {
        // O backend só recusa quando há estado remoto, vínculo ou histórico a
        // preservar. Nesse caso o card continua disponível para ação manual.
      }
    }
    setFeedback(
      params.get('message') ||
        'A autorização não foi concluída. Tente novamente.',
      'error'
    );
  } else if (entry.outcome === 'connected') {
    const inboxId = params.get('wame_inbox_id');
    if (!/^\d+$/.test(inboxId || '')) {
      setFeedback(
        'A Meta concluiu o retorno, mas não foi possível identificar a caixa de entrada. Use “Verificar agora” no canal.',
        'error'
      );
      return;
    }

    try {
      const status = await confirmReturnedSocialChannel(inboxId);
      if (status === 'connected') {
        setFeedback('Canal confirmado: já está recebendo mensagens.');
      } else if (status === 'awaiting') {
        setFeedback(
          'Autorização confirmada. Agora aguardamos a primeira mensagem para identificar a conta.'
        );
      } else {
        setFeedback(
          'Autorização recebida. A confirmação pode levar alguns minutos e será atualizada automaticamente.'
        );
      }
      store.dispatch('inboxes/get');
      await loadAuxiliaryIntegrations();
    } catch (error) {
      setFeedback(
        'A autorização voltou da Meta, mas não foi possível confirmar o estado agora. Use “Verificar agora” no canal.',
        'error'
      );
    }
  }
};

const readLinkedinAuthResult = () => {
  const outcome = route.query.linkedin;
  if (!outcome) return;

  activeTab.value = 'social';
  activeSocialKey.value = 'linkedin';
  // Limpa antes de mostrar: F5 não pode repetir o aviso.
  router.replace({
    name: route.name,
    params: route.params,
    query: { aba: 'social', canal: 'linkedin' },
  });

  setFeedback(
    LINKEDIN_RESULT_MESSAGES[outcome] || LINKEDIN_RESULT_MESSAGES.unavailable,
    outcome === 'connected' ? 'success' : 'error'
  );
};

// Um canal que ainda não está conectado é um canal em movimento: ou a
// autorização está em curso, ou ele espera a primeira mensagem para se
// identificar. Só nesse estado a releitura curta se paga.
const socialHasPending = computed(() =>
  wameChannels.value.some(channel =>
    ['pending', 'awaiting'].includes(channel.status)
  )
);

// Sem `immediate`: o watch roda no setup, ANTES de onMounted/onActivated, e um
// relógio criado ali nasce fora do ciclo de vida — uma instância que o router
// monte e descarte sem chegar a ativar deixa o timer girando para sempre.
// Medido em 04/09/2026: com o `immediate`, dois timers de 15s sobreviviam; sem
// ele, um. Quem liga o relógio na entrada é o onActivated, logo abaixo.
watch([() => activeTab.value, socialHasPending], ([tab, pending]) => {
  if (tab !== 'social') {
    stopSocialPolling();
    return;
  }
  if (pending) startSocialPolling();
  else stopSocialPolling();
});

// Entrar na aba já traz o estado do momento — inclusive o espelho do Aceleres,
// que os cards das redes ainda administradas por lá usam.
watch(
  () => activeTab.value,
  tab => {
    if (tab !== 'social') return;
    refreshSocialChannels();
    syncSocialChannels();
  }
);

/**
 * A página fica em `keep-alive`: sair dela não a destrói, e `onUnmounted` não
 * roda. Sem parar os relógios aqui, cada visita deixa para trás uma instância
 * viva consultando o servidor para sempre — medido em 04/09/2026 na aba de
 * canais sociais: **3 timers de 15s e 2 de 10s ativos ao mesmo tempo**, com uma
 * só página no DOM, e a releitura dos canais saindo em rajadas de três.
 *
 * Retomar na ativação mantém o comportamento visível igual ao de antes: quem
 * está olhando a tela continua vendo o estado se atualizar sozinho.
 */
onActivated(() => {
  isPageActive.value = true;
  loadKeyState();
  if (keyRefreshTimer) window.clearInterval(keyRefreshTimer);
  keyRefreshTimer = window.setInterval(loadKeyState, 20000);
  startLivePolling();
  if (activeTab.value === 'social' && socialHasPending.value)
    startSocialPolling();
  // Um pareamento em curso quando se saiu da tela continua sendo um pareamento
  // em curso ao voltar: sem isto o QR ficaria aberto sem ninguém conferindo.
  if (qrConnection.value?.instanceId)
    startQrPolling(qrConnection.value.instanceId);
});
onDeactivated(() => {
  isPageActive.value = false;
  if (keyRefreshTimer) window.clearInterval(keyRefreshTimer);
  keyRefreshTimer = null;
  stopLivePolling();
  stopSocialPolling();
  stopQrPolling();
});

// Navegação dentro da aplicação usa o mesmo modal de confirmação das ações.
// Recarregar ou fechar a aba mantém a proteção beforeunload do navegador.
const podeSairComEdicaoPendente = () => {
  if (!hasPendingGroupChanges.value) return true;
  if (resolveRouteDiscard) return false;
  return new Promise(resolve => {
    resolveRouteDiscard = resolve;
    openActionConfirmation({
      kind: 'route-discard',
      title: 'Descartar alterações e sair?',
      description:
        'Você mudou quais grupos entram no atendimento e ainda não salvou. Sair agora descarta essa mudança.',
      confirmLabel: 'Descartar e sair',
      loadingKey: 'route-discard',
    });
  });
};

onBeforeRouteLeave(() => podeSairComEdicaoPendente());

const avisarAntesDeFecharAba = event => {
  if (!hasPendingGroupChanges.value) return;
  event.preventDefault();
  // Navegadores atuais ignoram a mensagem e mostram um texto próprio; o que
  // importa é `returnValue` estar definido para o aviso aparecer.
  event.returnValue = '';
};

onMounted(async () => {
  window.addEventListener('beforeunload', avisarAntesDeFecharAba);
  store.dispatch('inboxes/get');
  readSocialAuthResult();
  readLinkedinAuthResult();
  loadHub({ useCache: true });
  loadKeyState();
  keyRefreshTimer = window.setInterval(loadKeyState, 20000);
  startLivePolling();
  emitter.on(BUS_EVENTS.ACELERES_WAME_POOL_UPDATED, updateWamePool);
});

onUnmounted(() => {
  resolveRouteDiscard?.(false);
  resolveRouteDiscard = null;
  if (keyRefreshTimer) window.clearInterval(keyRefreshTimer);
  window.removeEventListener('beforeunload', avisarAntesDeFecharAba);
  stopQrPolling();
  stopLivePolling();
  stopSocialPolling();
  emitter.off(BUS_EVENTS.ACELERES_WAME_POOL_UPDATED, updateWamePool);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
  <main class="integracoes-page flex w-full flex-col gap-4 px-4 py-4 md:px-10">
    <!--
      Cabecalho no mesmo padrao da pagina de Usuarios (settings/agents/Index.vue):
      teleportado para o outlet fixo do SettingsWrapper, que e quem desenha a
      borda inferior. As margens laterais tambem vem de la, iguais as de Usuarios.
    -->
    <Teleport
      v-if="isPageActive"
      defer
      to=".settings-wrapper__teams-header-outlet"
    >
      <header
        class="integracoes-page__header acl-mobile-account-menu-host flex flex-shrink-0 items-center justify-between gap-4 px-4 py-4 transition-[padding] duration-200 ease-out max-md:-mx-10 md:px-6"
      >
        <div class="integracoes-page__header-content min-w-0 max-md:w-full">
          <h1
            class="integracoes-page__title truncate text-[25px] font-bold leading-7 text-n-slate-12 max-md:flex-1 max-md:pr-[52px] max-md:text-center md:text-[21px]"
          >
            Integrações
          </h1>
          <p
            class="integracoes-page__subtitle mt-2 text-base text-n-slate-11 max-md:text-center md:mt-1"
          >
            Gerencie canais, responsáveis e conexões em um único lugar, sem
            perder o histórico da sua operação.
          </p>
        </div>
      </header>
    </Teleport>

    <Dialog
      ref="newConnectionDialog"
      width="md"
      title="Nova conexão"
      description="Escolha o canal que deseja conectar. Cada canal será configurado no seu próprio fluxo."
      :show-confirm-button="false"
      cancel-button-label="Fechar"
    >
      <div class="grid gap-2">
        <button
          type="button"
          class="flex items-center gap-3 rounded-2xl border border-n-weak p-3 text-left hover:border-n-brand hover:bg-n-alpha-1"
          @click="openGlobalConnection('whatsapp')"
        >
          <i class="i-ri-whatsapp-line text-xl text-n-teal-11" />
          <span>
            <strong class="block text-n-slate-12">WhatsApp</strong>
            <small class="text-n-slate-11"
              >Conectar um número por QR Code.</small
            >
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-3 rounded-2xl border border-n-weak p-3 text-left hover:border-n-brand hover:bg-n-alpha-1"
          @click="openGlobalConnection('instagram')"
        >
          <i class="i-ri-instagram-line text-xl text-n-ruby-11" />
          <span>
            <strong class="block text-n-slate-12">Instagram</strong>
            <small class="text-n-slate-11"
              >Criar ou reutilizar uma inbox corporativa.</small
            >
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-3 rounded-2xl border border-n-weak p-3 text-left hover:border-n-brand hover:bg-n-alpha-1"
          @click="openGlobalConnection('messenger')"
        >
          <i class="i-ri-messenger-line text-xl text-n-blue-11" />
          <span>
            <strong class="block text-n-slate-12">Messenger</strong>
            <small class="text-n-slate-11"
              >Criar ou reutilizar uma inbox corporativa.</small
            >
          </span>
        </button>
      </div>
    </Dialog>

    <WhatsappContractDialog
      v-if="contractRequestOpen && keyState"
      :state="keyState"
      @close="contractRequestOpen = false"
      @refresh="next => (keyState = next)"
    />
    <WhatsappKeyRequestDialog
      v-if="keyRequestOpen && keyState"
      :state="keyState"
      :manager="viewerIsManager"
      @close="keyRequestOpen = false"
      @refresh="next => (keyState = next)"
      @connect="createWizardRef?.open()"
      @manage="selectTab('chaves')"
    />

    <nav
      class="integracoes-page__tabs grid w-full grid-cols-2 gap-2 overflow-visible bg-transparent p-0 md:flex md:gap-1 md:overflow-x-auto md:rounded-[10px] md:border md:border-n-weak md:bg-n-solid-1 md:p-1"
    >
      <!--
        Aba ativa: borda + texto na cor da marca. `n-blue-11` e `n-brand`
        resolvem para o verde da marca (nao ha token `n-green` no Chatwoot),
        e e o mesmo par usado na pagina de Usuarios.
      -->
      <button
        v-for="tab in visibleTabs"
        :key="tab.key"
        type="button"
        class="integracoes-page__tab flex flex-shrink-0 items-center gap-1.5 rounded-xl border border-transparent px-4 py-2.5 text-sm font-medium transition-all duration-500 max-md:justify-center md:rounded-[8px] md:py-2 motion-reduce:transition-none"
        :class="[
          activeTab === tab.key
            ? 'integracoes-page__tab--active border-n-brand bg-n-brand/10 text-n-blue-11 shadow-sm'
            : 'text-n-slate-11 hover:bg-n-alpha-2 max-md:border-n-weak max-md:bg-n-solid-1 max-md:shadow-sm',
          /*
            O grid do mobile tem 2 colunas: so quando o numero de abas e impar
            a ultima sobra sozinha na linha e precisa ser centralizada. Com um
            numero par ela ocupa a segunda coluna normalmente.
          */
          visibleTabs.length % 2 === 1
            ? 'max-md:last:col-span-2 max-md:last:w-[calc(50%-0.25rem)] max-md:last:justify-self-center'
            : '',
        ]"
        @click="selectTab(tab.key)"
      >
        <i :class="tab.icon" class="integracoes-page__tab-icon text-base" />
        {{ tab.label }}
        <span
          v-if="tab.key === 'chaves' && keyState?.requests?.length"
          class="rounded-full bg-[#B42318] px-1.5 text-[10px] text-white"
          >{{ keyState.requests.length }}</span
        >
      </button>
    </nav>

    <WhatsappKeysPanel
      v-if="activeTab === 'chaves' && viewerIsManager && keyState"
      :state="keyState"
      :highlight-id="route.query.pedido"
      @refresh="next => (keyState = next)"
    />

    <ManagedInboxesPanel
      v-if="activeTab === 'inboxes' && viewerCanManageInboxes"
      @refresh-keys="
        loadKeyState();
        loadHub();
      "
    />

    <section
      v-show="activeTab === 'whatsapp'"
      class="integracoes-page__panel integracoes-page__panel--whatsapp rounded-3xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
    >
      <div
        class="integracoes-whatsapp__header flex flex-wrap items-start justify-between gap-4"
      >
        <div class="integracoes-whatsapp__header-text">
          <p
            class="integracoes-whatsapp__eyebrow text-xs font-semibold uppercase tracking-[0.2em] text-n-slate-10"
          >
            Canal de atendimento
          </p>
          <h2
            class="integracoes-whatsapp__title mt-1 text-xl font-semibold text-n-slate-12"
          >
            WhatsApp
          </h2>
          <p
            class="integracoes-whatsapp__description mt-1 max-w-2xl text-sm text-n-slate-10"
          >
            Escolha a tecnologia de conexão sem perder a caixa de entrada nem o
            histórico existente.
          </p>
        </div>
        <div
          class="integracoes-whatsapp__header-actions flex flex-wrap items-center gap-2"
        >
          <!--
            Selo de vagas do pool. Fica na mesma linha do botao (h-10, a altura
            do Button md) e carrega so a contagem; a explicacao completa mora no
            popover do (i), porque a frase inteira nao cabe numa pilula.
          -->
          <div
            v-if="keyState"
            v-on-clickaway="() => (poolInfoOpen = false)"
            class="relative"
          >
            <div
              class="flex h-[34px] items-center rounded-lg border bg-n-solid-1 text-[11px]"
              :class="
                (viewerIsManager ? keyFree === 0 : keyFreeSlots === 0)
                  ? 'border-[#F3D9B8]'
                  : 'border-[#E4E6EA]'
              "
            >
              <div
                class="flex items-center gap-2 px-3"
                :class="
                  (viewerIsManager ? keyFree === 0 : keyFreeSlots === 0)
                    ? 'text-[#9A4B00]'
                    : ''
                "
              >
                <i
                  :class="
                    (viewerIsManager ? keyFree === 0 : keyFreeSlots === 0)
                      ? 'i-lucide-triangle-alert'
                      : 'i-lucide-key-round text-n-slate-10'
                  "
                />
                <span v-if="viewerIsManager"
                  ><strong
                    >{{ keyState.used }} de {{ keyState.contracted }}</strong
                  >
                  chaves em uso · <strong>{{ keyFree }}</strong> livres</span
                >
                <span v-else
                  >Suas chaves:
                  <strong>{{ keyUsed }} de {{ keyLimit }}</strong> em uso</span
                >
              </div>
              <button
                v-if="viewerIsManager"
                title="Gerenciar chaves"
                aria-label="Gerenciar chaves"
                :aria-expanded="poolInfoOpen"
                class="flex h-full w-9 shrink-0 items-center justify-center border-l border-n-weak !p-0 text-[#9A4B00]"
                :class="keyFree === 0 ? 'bg-[#FFF8EF]' : ''"
                @click="poolInfoOpen = !poolInfoOpen"
              >
                <i class="i-lucide-key-round text-base" />
              </button>
            </div>
            <div
              v-if="viewerIsManager && poolInfoOpen"
              class="absolute right-0 top-full z-20 mt-2 w-[280px] rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-lg"
            >
              <h3 class="text-sm font-semibold">Chaves da empresa</h3>
              <dl class="mt-3 space-y-2 text-xs text-n-slate-11">
                <div class="flex justify-between">
                  <dt>Contratadas pela empresa</dt>
                  <dd class="font-semibold">{{ keyState.contracted }}</dd>
                </div>
                <div class="flex justify-between">
                  <dt>Distribuídas a pessoas</dt>
                  <dd class="font-semibold">{{ keyState.distributed }}</dd>
                </div>
                <div class="flex justify-between">
                  <dt>Em uso</dt>
                  <dd class="font-semibold">{{ keyState.used }}</dd>
                </div>
                <div class="flex justify-between">
                  <dt>Livres</dt>
                  <dd
                    class="font-semibold"
                    :class="keyFree === 0 ? 'text-[#9A4B00]' : ''"
                  >
                    {{ keyFree }}
                  </dd>
                </div>
              </dl>
              <button
                class="mt-4 w-full border-t border-n-weak pt-3 text-left text-xs font-semibold text-[#15803D]"
                @click="
                  poolInfoOpen = false;
                  selectTab('chaves');
                "
              >
                Gerenciar chaves →
              </button>
            </div>
          </div>

          <!--
            O atalho para o cadastro nativo de WhatsApp oficial do Chatwoot foi
            retirado: ele levava para fora deste hub, para um fluxo que não passa
            pelo executor do Aceleres e portanto não cria a WhatsappInstance, não
            reserva vaga do pool nem aplica o recorte de visibilidade. Uma conexão
            criada por lá não aparecia nesta lista.
          -->
          <Button
            class="integracoes-whatsapp__new-connection !h-[34px]"
            label="Nova conexão WhatsApp"
            icon="i-lucide-plus"
            @click="openNewWhatsapp"
          />
          <p
            v-if="keyRequestError"
            role="alert"
            class="w-full text-xs text-n-ruby-11"
          >
            {{ keyRequestError }}
          </p>
        </div>
      </div>

      <p
        v-if="keyRequestError"
        class="mt-2 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ keyRequestError }}
      </p>

      <!-- Sem `v-if` de propósito: o assistente também é a superfície do QR de
           uma inbox que já existe, e reconectar não exige poder criar. -->
      <WhatsappConnectionWizard
        ref="createWizardRef"
        class="integracoes-whatsapp__wizard"
        :sellers="sellers"
        :providers="whatsappProviders"
        :is-loading="Boolean(actionLoading.create)"
        :pairing="qrConnection"
        @create="createWhatsapp"
        @refresh-qr="refreshPairingCode"
        @cancel-pairing="
          qrConnection && cancelConnectAttempt(qrConnection.instanceId)
        "
        @close="onPairingDialogClosed"
      />

      <div
        v-if="!loading && instances.length"
        class="integracoes-whatsapp__search relative mt-5"
      >
        <i
          class="integracoes-whatsapp__search-icon i-lucide-search pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-10 ltr:left-3 rtl:right-3"
        />
        <input
          v-model="connectionSearch"
          type="search"
          placeholder="Buscar conexão por nome ou número"
          class="integracoes-whatsapp__search-input !m-0 h-10 w-full rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:border-n-brand ltr:!pl-9 ltr:!pr-3 rtl:!pr-9 rtl:!pl-3"
        />
      </div>

      <div
        v-if="loading"
        class="integracoes-whatsapp__loading py-12 text-center text-sm text-n-slate-10"
      >
        Carregando conexões…
      </div>
      <div
        v-else-if="whatsappLoadFailed"
        class="integracoes-whatsapp__load-error mt-5 rounded-2xl border border-n-ruby-5 p-7 text-center"
      >
        <p class="font-medium text-n-slate-12">
          {{
            whatsappLoadFailureMessage ||
            'Não foi possível carregar as conexões de WhatsApp.'
          }}
        </p>
        <Button
          class="mx-auto mt-4"
          size="sm"
          label="Tentar novamente"
          icon="i-lucide-refresh-cw"
          @click="loadHub()"
        />
      </div>
      <div
        v-else-if="!instances.length"
        class="integracoes-whatsapp__empty mt-5 rounded-2xl border border-dashed border-n-weak p-7 text-center"
      >
        <i
          class="integracoes-whatsapp__empty-icon i-ri-whatsapp-line text-3xl text-n-slate-10"
        />
        <p
          class="integracoes-whatsapp__empty-title mt-2 font-medium text-n-slate-12"
        >
          Nenhuma conexão cadastrada
        </p>
        <p
          class="integracoes-whatsapp__empty-text mt-1 text-sm text-n-slate-10"
        >
          {{
            viewerCanCreate
              ? 'Crie uma conexão para iniciar o pareamento.'
              : 'Nenhuma conexão compartilhada foi liberada para sua operação.'
          }}
        </p>
      </div>
      <p
        v-else-if="!whatsappSections.length"
        class="integracoes-whatsapp__no-results mt-6 py-12 text-center text-sm text-n-slate-10"
      >
        Nenhuma conexão encontrada para “{{ connectionSearch }}”.
      </p>
      <div v-else class="integracoes-whatsapp__groups mt-6 grid gap-6">
        <section
          v-for="section in whatsappSections"
          :key="section.key"
          class="integracoes-whatsapp__group grid gap-3"
        >
          <div
            class="integracoes-whatsapp__group-header flex items-start gap-3"
          >
            <span
              class="integracoes-whatsapp__group-icon mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <span :class="section.icon" class="size-4" aria-hidden="true" />
            </span>
            <div class="integracoes-whatsapp__group-heading">
              <h3
                class="integracoes-whatsapp__group-title font-semibold text-n-slate-12"
              >
                {{ section.title }}
              </h3>
              <p
                class="integracoes-whatsapp__group-description mt-0.5 text-sm text-n-slate-10"
              >
                {{ section.description }}
              </p>
            </div>
          </div>

          <div
            class="integracoes-whatsapp__cards grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
          >
            <ChannelConnectionCard
              v-for="instance in section.instances"
              :key="instance.id"
              class="integracoes-whatsapp__card"
              icon="i-ri-whatsapp-line"
              :icon-gradient="WHATSAPP_ICON_GRADIENT"
              :title="instance.name"
              subtitle-icon="i-lucide-phone"
              :subtitle="
                instance.phone_number
                  ? formatPhoneNumber(instance.phone_number)
                  : instance.label || 'Aguardando pareamento'
              "
              :status-label="
                statusLabel(instance.status, instance.migration_status)
              "
              :status-class="statusClass(instance.status)"
              :badges="[
                ...(instance.is_primary
                  ? [
                      {
                        label: 'Principal',
                        class: 'bg-n-brand/15 text-n-brand',
                      },
                    ]
                  : []),
                ...(instance.disconnect_alert_muted
                  ? [
                      {
                        label: disconnectAlertMutedLabel(instance),
                        class: 'bg-n-amber-3 text-n-amber-11',
                      },
                    ]
                  : []),
                ...(!instance.can_manage
                  ? [
                      {
                        label: instance.can_connect
                          ? 'Responsável pela conexão'
                          : 'Somente leitura',
                        class: 'bg-n-alpha-2 text-n-slate-10',
                      },
                    ]
                  : []),
              ]"
              :details="whatsappDetails(instance)"
              :meta-line="whatsappMetaLine(instance)"
              :notice="
                instance.can_manage && instance.can_release_key
                  ? keyReleaseNotice(instance)
                  : ''
              "
            >
              <!--
                Desconectar, Tornar principal, Configurar e Remover moram no
                menu de tres pontos do cabecalho. Aqui em baixo fica so a acao
                de entrada do cartao (conectar/cancelar/revisar), que precisa
                estar visivel sem abrir menu nenhum.
              -->
              <template #menu>
                <div
                  v-if="instance.can_manage"
                  v-on-clickaway="() => closeCardMenu(instance)"
                  class="integracoes-whatsapp__card-menu group relative flex items-center"
                >
                  <!--
                    Botao cru em vez do <Button>: qualquer variante dele traz
                    fundo ou borda, e aqui o gatilho e so o icone.
                  -->
                  <button
                    type="button"
                    aria-label="Ações da conexão"
                    class="integracoes-whatsapp__card-menu-trigger flex h-8 w-8 items-center justify-center border-none bg-transparent p-0 text-n-slate-11 transition-colors hover:text-n-slate-12"
                    @click="toggleCardMenu(instance)"
                  >
                    <i class="i-lucide-ellipsis-vertical text-xl" />
                  </button>
                  <DropdownMenu
                    v-if="openCardMenuId === instance.id"
                    :menu-items="cardMenuItems(instance)"
                    class="integracoes-whatsapp__card-menu-list top-full z-50 mt-1 ltr:right-0 rtl:left-0"
                    @action="handleCardMenuAction($event, instance)"
                  />
                </div>
              </template>
              <Button
                v-if="instance.disconnect_alert_muted"
                slate
                faded
                size="sm"
                class="integracoes-whatsapp__card-action integracoes-whatsapp__card-action--reactivate-alert"
                label="Reativar aviso"
                icon="i-lucide-bell-ring"
                :is-loading="actionLoading[`disconnect-alert-${instance.id}`]"
                @click="reactivateDisconnectAlert(instance)"
              />
              <template v-if="instance.can_connect">
                <Button
                  v-if="
                    instance.status === 'migration_pending' &&
                    instance.can_manage
                  "
                  amber
                  size="sm"
                  class="integracoes-whatsapp__card-action integracoes-whatsapp__card-action--review-migration"
                  :label="migrationCardActionLabel(instance)"
                  icon="i-lucide-git-merge"
                  @click="openSettings(instance)"
                />
                <Button
                  v-else-if="
                    instance.status !== 'migration_pending' &&
                    !['connected', 'connecting'].includes(instance.status)
                  "
                  size="sm"
                  class="integracoes-whatsapp__card-action integracoes-whatsapp__card-action--connect"
                  label="Conectar"
                  icon="i-lucide-qr-code"
                  :is-loading="actionLoading[`connect-${instance.id}`]"
                  @click="connectWhatsapp(instance, { openWizard: true })"
                />
                <Button
                  v-else-if="instance.status === 'connecting'"
                  slate
                  size="sm"
                  class="integracoes-whatsapp__card-action integracoes-whatsapp__card-action--cancel"
                  label="Cancelar tentativa"
                  icon="i-lucide-x-circle"
                  :disabled="actionLoading[`connect-${instance.id}`]"
                  :is-loading="actionLoading[`cancel-${instance.id}`]"
                  @click="cancelConnectAttempt(instance.id)"
                />
                <p
                  v-if="instance.status === 'migration_pending'"
                  class="integracoes-whatsapp__migration-card-note text-xs text-n-slate-10"
                >
                  {{
                    ['queued', 'running'].includes(instance.migration_status)
                      ? 'A consolidação está sendo processada. Acompanhe o andamento antes de iniciar outra conexão.'
                      : 'Este número já está conectado em outra instância. Revise a consolidação para preservar o histórico antes de prosseguir.'
                  }}
                </p>
              </template>
              <span
                v-else
                class="integracoes-whatsapp__card-readonly text-xs text-n-slate-10"
              >
                {{
                  viewerIsSupervisor
                    ? 'Supervisor acompanha sem alterar esta conexão.'
                    : 'Conexão compartilhada pela sua operação.'
                }}
              </span>
            </ChannelConnectionCard>
          </div>
        </section>
      </div>

      <Dialog
        ref="settingsDialog"
        class="integracoes-whatsapp__settings-dialog integracoes-whatsapp__settings-dialog--mobile-responsive max-md:!m-auto max-md:!max-h-[calc(100dvh-2rem)]"
        panel-class="integracoes-whatsapp__settings-panel integracoes-whatsapp__settings-panel--mobile-responsive max-md:gap-4 max-md:p-4"
        type="edit"
        :title="
          selectedInstance
            ? `Gerenciar ${selectedInstance.name}`
            : 'Gerenciar conexão'
        "
        description="Configure identificação, responsável e tecnologia desta conexão."
        width="3xl"
        position="top"
        mobile-inset
        cancel-button-label="Fechar"
        confirm-button-label="Salvar conexão"
        overflow-y-auto
        :is-loading="
          !!selectedInstance && actionLoading[`settings-${selectedInstance.id}`]
        "
        @confirm="requestSaveSettings"
        @close="requestCloseSettings"
      >
        <div v-if="selectedInstance" class="integracoes-settings space-y-5">
          <div
            class="integracoes-settings__identity grid gap-4 rounded-2xl border border-n-weak bg-n-alpha-1 p-4 max-md:gap-3 max-md:p-3 md:grid-cols-2"
          >
            <label
              class="integracoes-settings__field integracoes-settings__field--name grid gap-1 text-sm font-medium text-n-slate-12"
              >Nome<input
                v-model="editForm.display_name"
                class="integracoes-settings__input rounded-xl border border-n-weak bg-n-background px-3 py-2"
            /></label>
            <label
              class="integracoes-settings__field integracoes-settings__field--type grid gap-1 text-sm font-medium text-n-slate-12"
              >Tipo<select
                v-model="editForm.connection_type"
                class="integracoes-settings__select rounded-xl border border-n-weak bg-n-background px-3 py-2"
              >
                <option
                  v-for="type in connectionTypes"
                  :key="type.value"
                  :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select></label
            >
            <label
              v-if="editForm.connection_type === 'individual_company'"
              class="integracoes-settings__field integracoes-settings__field--assignee grid gap-1 text-sm font-medium text-n-slate-12"
              >Responsável<select
                v-model="editForm.default_assignee_user_id"
                class="integracoes-settings__select rounded-xl border border-n-weak bg-n-background px-3 py-2"
              >
                <option value="">Selecione</option>
                <option
                  v-for="seller in sellers"
                  :key="seller.id"
                  :value="seller.id"
                >
                  {{ seller.name }}
                </option>
              </select></label
            >
            <label
              v-if="editForm.connection_api !== 'api1'"
              class="integracoes-settings__field integracoes-settings__field--api grid gap-1 text-sm font-medium text-n-slate-12"
              >Tecnologia<select
                v-model="editForm.connection_api"
                class="integracoes-settings__select rounded-xl border border-n-weak bg-n-background px-3 py-2"
              >
                <option value="api2">Conexão Zoraxy</option>
                <option value="zapi">Z-API</option>
              </select></label
            >
            <div
              v-else
              class="integracoes-settings__legacy-api grid gap-1 rounded-xl border border-n-amber-5 bg-n-amber-2 px-3 py-2 text-sm"
            >
              <span
                class="integracoes-settings__legacy-api-title font-medium text-n-slate-12"
                >Tecnologia</span
              >
              <span
                class="integracoes-settings__legacy-api-text text-n-slate-11"
              >
                Conexão legada. Use a consolidação para migrar para a Conexão
                Zoraxy.
              </span>
            </div>
          </div>
          <section
            v-if="
              editForm.connection_type === 'shared' &&
              selectedInstance.can_manage_team_access
            "
            class="integracoes-settings__teams rounded-2xl border border-n-weak bg-n-background p-4"
          >
            <div
              class="integracoes-settings__teams-header flex flex-wrap items-start justify-between gap-3"
            >
              <div class="integracoes-settings__teams-heading">
                <h4
                  class="integracoes-settings__teams-title font-semibold text-n-slate-12"
                >
                  Equipes com acesso
                </h4>
                <p
                  class="integracoes-settings__teams-description mt-1 text-sm text-n-slate-10"
                >
                  <template v-if="selectedInstance.origin_team">
                    A equipe de origem fica sempre selecionada. As demais
                    recebem somente visibilidade; o roteamento não muda.
                  </template>
                  <template v-else>
                    Selecione uma equipe da qual você participa para definir a
                    origem. As demais recebem somente visibilidade; o roteamento
                    continua na equipe de origem.
                  </template>
                </p>
              </div>
              <span
                v-if="selectedInstance.origin_team"
                class="integracoes-settings__teams-origin rounded-full bg-n-teal-2 px-2.5 py-1 text-xs font-medium text-n-teal-11"
              >
                Origem: {{ selectedInstance.origin_team.name }}
              </span>
            </div>
            <label
              v-if="!selectedInstance.origin_team"
              class="integracoes-settings__teams-origin-select mt-4 grid gap-1 text-sm font-medium text-n-slate-12"
            >
              Equipe de origem
              <select
                v-model.number="editForm.origin_team_id"
                class="integracoes-settings__select rounded-xl border border-n-weak bg-n-background px-3 py-2"
              >
                <option value="">Selecione uma equipe</option>
                <option
                  v-for="team in originEligibleTeamOptions"
                  :key="team.id"
                  :value="team.id"
                >
                  {{ team.name }}
                </option>
              </select>
              <span
                v-if="!originEligibleTeamOptions.length"
                class="text-xs font-normal text-n-ruby-11"
              >
                O gestor original não participa de nenhuma equipe desta conta.
                Inclua-o em uma equipe antes de salvar a conexão compartilhada.
              </span>
            </label>
            <label
              class="integracoes-settings__teams-search mt-4 grid gap-1 text-sm text-n-slate-12"
            >
              Pesquisar equipe ou integrante
              <input
                v-model="teamSearch"
                type="search"
                placeholder="Ex.: comercial, Vanessa…"
                class="integracoes-settings__teams-search-input rounded-xl border border-n-weak bg-n-background px-3 py-2"
              />
            </label>
            <div
              class="integracoes-settings__teams-list mt-3 grid max-h-64 gap-2 overflow-y-auto pr-1 sm:grid-cols-2"
            >
              <label
                v-for="team in filteredTeamOptions"
                :key="team.id"
                class="integracoes-settings__team flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak p-3 hover:bg-n-alpha-1"
                :class="{
                  'integracoes-settings__team--locked cursor-not-allowed bg-n-alpha-1':
                    isOriginTeam(team.id),
                }"
              >
                <input
                  class="integracoes-settings__team-checkbox mt-1"
                  type="checkbox"
                  :checked="hasTeamAccess(team.id)"
                  :disabled="isOriginTeam(team.id)"
                  @change="toggleTeamAccess(team.id)"
                />
                <span class="integracoes-settings__team-body min-w-0">
                  <span
                    class="integracoes-settings__team-name block font-medium text-n-slate-12"
                  >
                    {{ team.name }}
                  </span>
                  <span
                    class="integracoes-settings__team-members block truncate text-xs text-n-slate-10"
                  >
                    {{ team.member_count }} integrante(s)
                    <template v-if="team.member_names?.length">
                      · {{ team.member_names.join(', ') }}
                    </template>
                  </span>
                </span>
              </label>
            </div>
            <p
              v-if="!filteredTeamOptions.length"
              class="integracoes-settings__teams-empty mt-3 text-sm text-n-slate-10"
            >
              Nenhuma equipe encontrada.
            </p>
          </section>
          <div class="integracoes-settings__group-actions flex flex-wrap gap-2">
            <Button
              slate
              size="sm"
              type="button"
              class="integracoes-settings__groups-load"
              label="Grupos"
              :is-loading="actionLoading[`groups-${selectedInstance.id}`]"
              @click="loadGroups(selectedInstance.id)"
            />
            <!--
              O backend recusa a sincronização com 409 enquanto os grupos
              estiverem desligados ("Ative os grupos antes de solicitar uma
              sincronização"), e desligar apaga a lista — então o botão ficava
              convidando para o único clique que não pode dar certo. Enquanto o
              painel não foi carregado não há o que afirmar, e ele segue ativo.
            -->
            <Button
              slate
              size="sm"
              type="button"
              class="integracoes-settings__groups-sync"
              label="Sincronizar grupos"
              :disabled="
                Boolean(groupConfig) && !groupConfig.config.groups_enabled
              "
              :title="
                Boolean(groupConfig) && !groupConfig.config.groups_enabled
                  ? 'Ligue \'Receber grupos\' e salve antes de sincronizar.'
                  : ''
              "
              :is-loading="actionLoading[`groups-sync-${selectedInstance.id}`]"
              @click="syncGroups(selectedInstance.id)"
            />
          </div>

          <div
            v-if="groupConfig"
            class="integracoes-settings__groups rounded-2xl border border-n-weak bg-n-background p-4"
          >
            <h4
              class="integracoes-settings__groups-title font-semibold text-n-slate-12"
            >
              Grupos WhatsApp
            </h4>
            <p
              v-if="groupConfig.warning"
              role="status"
              data-testid="groups-sync-warning"
              class="mt-3 rounded-lg border border-n-weak bg-n-background p-3 text-sm text-n-slate-12"
            >
              {{ groupConfig.warning }}
            </p>
            <div
              class="integracoes-settings__groups-modes mt-3 flex flex-wrap gap-4 text-sm text-n-slate-12"
            >
              <label
                class="integracoes-settings__groups-mode integracoes-settings__groups-mode--enabled flex items-center gap-2"
                ><input
                  v-model="groupConfig.config.groups_enabled"
                  type="checkbox"
                  @change="groupEditRevision += 1"
                />
                Receber grupos</label
              >
              <!--
                Modo e lista só existem com "Receber grupos" ligado: `saveGroups`
                manda `groups_enabled: false` e o backend descarta a seleção, então
                deixá-los clicáveis aqui é prometer uma escolha que morre no Salvar.
              -->
              <template v-if="groupConfig.config.groups_enabled">
                <label
                  class="integracoes-settings__groups-mode integracoes-settings__groups-mode--all flex items-center gap-2"
                  ><input
                    v-model="groupConfig.config.groups_mode"
                    value="all"
                    type="radio"
                    @change="groupEditRevision += 1"
                  />
                  Todos</label
                >
                <label
                  class="integracoes-settings__groups-mode integracoes-settings__groups-mode--specific flex items-center gap-2"
                  ><input
                    v-model="groupConfig.config.groups_mode"
                    value="specific"
                    type="radio"
                    @change="groupEditRevision += 1"
                  />
                  Apenas selecionados</label
                >
              </template>
            </div>
            <p
              v-if="
                !groupConfig.config.groups_enabled &&
                groupConfig.available_groups?.length
              "
              class="integracoes-settings__groups-disabled-notice mt-3 text-sm text-n-slate-10"
            >
              Esta conexão tem
              {{ groupConfig.available_groups.length }}
              grupo(s) detectado(s). Enquanto "Receber grupos" estiver
              desligado, as mensagens desses grupos não aparecem no atendimento.
            </p>
            <label
              v-if="
                groupConfig.config.groups_enabled &&
                groupConfig.config.groups_mode === 'specific' &&
                groupConfig.available_groups?.length
              "
              class="integracoes-settings__groups-search mt-4 grid gap-1 text-sm text-n-slate-12"
            >
              Pesquisar grupo
              <input
                v-model="groupSearch"
                type="search"
                placeholder="Ex.: obra, condomínio…"
                class="integracoes-settings__groups-search-input rounded-xl border border-n-weak bg-n-background px-3 py-2"
              />
            </label>
            <div
              v-if="
                groupConfig.config.groups_enabled &&
                groupConfig.config.groups_mode === 'specific' &&
                groupConfig.available_groups?.length
              "
              class="integracoes-settings__groups-list mt-3 grid max-h-56 gap-2 overflow-y-auto"
            >
              <label
                v-for="group in filteredGroupOptions"
                :key="group.group_jid"
                class="integracoes-settings__groups-item flex items-center gap-2 text-sm text-n-slate-12"
                ><input
                  :checked="
                    (groupConfig.config.allowed_jids || []).includes(
                      group.group_jid
                    )
                  "
                  type="checkbox"
                  @change="toggleGroup(group.group_jid)"
                />{{ group.subject || group.group_jid }}</label
              >
              <p
                v-if="!filteredGroupOptions.length"
                class="integracoes-settings__groups-no-results text-sm text-n-slate-10"
              >
                Nenhum grupo encontrado para “{{ groupSearch }}”.
              </p>
            </div>
            <p
              v-else-if="!groupConfig.available_groups?.length"
              class="integracoes-settings__groups-empty mt-3 text-sm text-n-slate-10"
            >
              Nenhum grupo sincronizado ainda.
            </p>
            <Button
              class="integracoes-settings__groups-save mt-4"
              size="sm"
              type="button"
              label="Salvar grupos"
              :is-loading="actionLoading[`groups-save-${selectedInstance.id}`]"
              @click="saveGroups()"
            />
          </div>

          <div
            v-if="migration"
            class="integracoes-settings__migration rounded-2xl border border-n-amber-5 bg-n-amber-2 p-4"
          >
            <h4
              class="integracoes-settings__migration-title font-semibold text-n-slate-12"
            >
              Consolidação de conexão
            </h4>
            <p
              class="integracoes-settings__migration-status mt-1 text-sm text-n-slate-11"
            >
              {{
                migration.error_message ||
                migrationPhaseLabel(migration.phase || migration.status)
              }}
            </p>
            <p
              v-if="migration.session_preserved"
              class="integracoes-settings__migration-note mt-2 text-xs text-n-slate-11"
            >
              A sessão remota e o histórico foram preservados.
            </p>
            <div
              class="integracoes-settings__migration-actions mt-4 flex flex-wrap gap-2"
            >
              <Button
                v-if="migration.can_confirm"
                amber
                size="sm"
                type="button"
                class="integracoes-settings__migration-confirm"
                label="Confirmar consolidação"
                :is-loading="actionLoading[`migration-${selectedInstance.id}`]"
                @click="confirmMigration"
              />
              <Button
                v-if="migration.can_cancel"
                slate
                size="sm"
                type="button"
                class="integracoes-settings__migration-cancel"
                label="Cancelar consolidação"
                :is-loading="
                  actionLoading[`migration-cancel-${selectedInstance.id}`]
                "
                @click="requestCancelMigration"
              />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        ref="deletionDialog"
        class="integracoes-delete-dialog"
        type="alert"
        mobile-inset
        overflow-y-auto
        :panel-class="mobileAlertPanelClass"
        :title="
          deletionPreview
            ? `Remover “${deletionPreview.instance.name}”?`
            : 'Remover conexão?'
        "
        description=""
        confirm-button-label="Remover e arquivar"
        cancel-button-label="Manter conexão"
        :is-loading="
          !!deletionPreview &&
          actionLoading[`delete-${deletionPreview.instance.id}`]
        "
        @confirm="confirmDeletion"
        @close="closeDeletionPreview"
      >
        <div
          v-if="deletionPreview"
          class="integracoes-delete space-y-3 text-sm text-n-slate-11"
        >
          <p class="integracoes-delete__intro">
            Remover é diferente de desconectar: a conexão sai da lista e a caixa
            de entrada é arquivada. O atendimento não é apagado — as conversas
            ficam resolvidas, continuam disponíveis para consulta e seguem
            ligadas ao contato.
          </p>
          <p
            v-if="deletionPreview.shared_with_other"
            class="integracoes-delete__shared rounded-xl bg-n-amber-2 p-3 text-n-amber-11"
          >
            Outra conexão ainda usa esta caixa de entrada: ela continua ativa e
            apenas este registro é removido.
          </p>
          <template v-else>
            <ul
              v-if="deletionPreview.preview_available"
              class="integracoes-delete__summary grid gap-1 rounded-xl bg-n-amber-2 p-3 text-n-amber-11 max-md:[&>li]:items-start max-md:[&>li]:gap-2 max-md:[&>li>span]:min-w-0 max-md:[&>li>strong]:shrink-0"
            >
              <li
                class="integracoes-delete__summary-item integracoes-delete__summary-item--archived flex justify-between gap-4 border-b border-n-amber-5/40 py-1"
              >
                <span class="integracoes-delete__summary-label"
                  >Conversas arquivadas (preservadas)</span
                >
                <strong class="integracoes-delete__summary-value">
                  {{ archivedConversationCount
                  }}{{ deletionPreview.count_capped ? '+' : '' }}
                </strong>
              </li>
              <li
                v-if="deletionPreview.purges_unassigned"
                class="integracoes-delete__summary-item integracoes-delete__summary-item--purged flex justify-between gap-4 border-b border-n-amber-5/40 py-1"
              >
                <span class="integracoes-delete__summary-label"
                  >Conversas sem atendente (apagadas por privacidade)</span
                >
                <strong class="integracoes-delete__summary-value">
                  {{ deletionPreview.unassigned_count
                  }}{{ deletionPreview.count_capped ? '+' : '' }}
                </strong>
              </li>
              <li
                class="integracoes-delete__summary-item integracoes-delete__summary-item--funil flex justify-between gap-4 py-1"
              >
                <span class="integracoes-delete__summary-label"
                  >Cards do funil arquivados</span
                >
                <strong class="integracoes-delete__summary-value">{{
                  deletionPreview.funil_cards_count
                }}</strong>
              </li>
            </ul>
            <p
              v-else
              class="integracoes-delete__summary-unavailable rounded-xl bg-n-amber-2 p-3 text-n-amber-11"
            >
              Não foi possível consultar o histórico desta caixa de entrada
              agora. O arquivamento acontece do mesmo jeito e nada é apagado.
            </p>
          </template>
        </div>
      </Dialog>

      <Dialog
        ref="feedbackDialog"
        class="integracoes-feedback-dialog"
        :type="feedbackType === 'error' ? 'alert' : 'edit'"
        mobile-inset
        width="sm"
        :title="feedbackTitle"
        :description="feedback"
        confirm-button-label="Entendi"
        :show-cancel-button="false"
        @confirm="dismissFeedback"
        @close="feedback = ''"
      />

      <Dialog
        ref="actionConfirmationDialog"
        class="integracoes-confirm-dialog"
        type="alert"
        mobile-inset
        overflow-y-auto
        :panel-class="mobileAlertPanelClass"
        :title="actionConfirmation?.title || 'Confirmar ação?'"
        :description="actionConfirmation?.description || ''"
        :confirm-button-label="actionConfirmation?.confirmLabel || 'Confirmar'"
        cancel-button-label="Voltar"
        :is-loading="
          !!actionConfirmation && !!actionLoading[actionConfirmation.loadingKey]
        "
        @confirm="confirmAction"
        @close="closeActionConfirmation"
      />

      <Dialog
        ref="socialRemovalDialog"
        class="integracoes-delete-dialog"
        type="alert"
        mobile-inset
        overflow-y-auto
        :panel-class="mobileAlertPanelClass"
        :title="
          socialRemovalPreview
            ? `Remover “${socialRemovalPreview.name}”?`
            : 'Remover canal?'
        "
        description=""
        confirm-button-label="Remover e arquivar"
        cancel-button-label="Manter conexão"
        :is-loading="
          !!socialRemovalPreview &&
          !!actionLoading[`wame-remove-${socialRemovalPreview.inbox_id}`]
        "
        :disable-confirm-button="!socialRemovalCodeMatches"
        @confirm="removeWameChannel"
        @close="closeWameRemovalPreview"
      >
        <div
          v-if="socialRemovalPreview"
          class="integracoes-delete space-y-4 text-sm text-n-slate-11"
        >
          <p class="integracoes-delete__intro">
            Remover é diferente de desconectar: o canal sai da lista e a caixa
            de entrada é arquivada. Nenhuma conversa ou mensagem será apagada; o
            histórico continuará disponível para consulta.
          </p>
          <ul
            class="integracoes-delete__summary grid gap-1 rounded-xl bg-n-amber-2 p-3 text-n-amber-11"
          >
            <li class="flex justify-between gap-4 py-1">
              <span>Conversas preservadas</span>
              <strong>{{ socialRemovalPreview.conversations_count }}</strong>
            </li>
            <li class="flex justify-between gap-4 py-1">
              <span>Cards do funil</span>
              <strong>serão arquivados</strong>
            </li>
          </ul>
          <div class="rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-3">
            <p class="text-n-ruby-11">
              Para confirmar, digite o código
              <strong class="font-mono text-base tracking-[0.2em]">
                {{ socialRemovalPreview.confirmation_code }}
              </strong>
            </p>
            <input
              v-model="socialRemovalCode"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="4"
              autocomplete="off"
              aria-label="Código de confirmação da remoção"
              placeholder="0000"
              class="reset-base mt-3 h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 font-mono text-base tracking-[0.3em] text-n-slate-12 outline-none focus:border-n-ruby-8"
              @input="
                socialRemovalCode = socialRemovalCode
                  .replace(/\D/g, '')
                  .slice(0, 4)
              "
            />
            <p class="mt-2 text-xs text-n-ruby-10">
              O código expira em 10 minutos e vale somente para esta conexão.
            </p>
          </div>
        </div>
      </Dialog>

      <Dialog
        ref="keyReleaseDialog"
        class="integracoes-key-release-dialog"
        type="alert"
        title="Devolver chave ao cofre de chaves?"
        description=""
        confirm-button-label="Devolver chave"
        cancel-button-label="Manter reserva"
        :is-loading="
          !!releaseConfirmation &&
          actionLoading[`release-${releaseConfirmation.id}`]
        "
        @confirm="confirmKeyRelease"
        @close="closeKeyReleaseConfirmation"
      >
        <div
          v-if="releaseConfirmation"
          class="integracoes-key-release space-y-3 text-sm text-n-slate-11"
        >
          <p class="integracoes-key-release__intro">
            A conexão
            <strong class="integracoes-key-release__name">{{
              releaseConfirmation.name
            }}</strong>
            está desconectada e ainda mantém uma chave reservada.
          </p>
          <p class="integracoes-key-release__explanation">
            Ao confirmar, o sistema confirma o encerramento da sessão,
            desvincula a chave desta instância e a devolve ao cofre de chaves da
            empresa.
          </p>
          <p
            class="integracoes-key-release__warning rounded-xl bg-n-amber-2 p-3 text-n-amber-11"
          >
            Para conectar este número novamente depois, será necessário ler um
            novo QR Code. A caixa de entrada e o histórico permanecem
            preservados.
          </p>
        </div>
      </Dialog>
    </section>

    <!-- Aceleres 2026-09-04: medido a 393px, o cartao de canal ficava com 285px
         — 108px do ecra, 27%, iam-se em molduras antes de comecar o conteudo.
         Sao tres camadas com afastamento: a pagina (px-4), este painel (p-5) e
         a seccao do canal (p-4). No telemovel o painel encolhe e a seccao la
         dentro perde a moldura por completo; no computador, onde ha largura de
         sobra, fica tudo como estava. -->
    <section
      v-show="activeTab === 'social'"
      class="rounded-3xl border border-n-weak bg-n-solid-1 p-5 shadow-sm max-md:p-3"
    >
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12">
            Canais sociais e mensageria
          </h2>
          <p class="mt-1 max-w-2xl text-sm text-n-slate-10">
            Conecte e acompanhe cada canal em um só lugar. Instagram e Messenger
            usam o fluxo protegido da Meta; o LinkedIn conecta a Company Page da
            empresa; os demais continuam na central do Acelerity durante a
            migração.
          </p>
          <p
            v-if="wameLoadFailed"
            class="mt-2 max-w-2xl rounded-xl bg-n-ruby-2 p-2.5 text-sm text-n-ruby-11"
          >
            Não foi possível ler o estado do Instagram e do Messenger agora. O
            que aparece abaixo pode estar incompleto — atualize a página.
          </p>
        </div>
        <!--
          Era "Atualizar canais". Um botão de atualizar só existe quando a tela
          não sabe se manter certa; esta agora se atualiza sozinha (postMessage
          da autorização, evento recebido pela ponte e releitura ao abrir a aba),
          e o que faltava era a porta de entrada de uma conexão nova.
        -->
        <Button
          v-if="viewerCanCreate && activeWameProvider"
          size="sm"
          :label="`Nova conexão ${activeSocialSection.channel.label}`"
          icon="i-lucide-plus"
          class="integracoes-social__new-channel"
          @click="openSocialWizard(activeWameProvider)"
        />
      </div>

      <div class="mt-5 flex flex-col gap-5 md:flex-row md:items-start">
        <!-- Sidebar de navegação: um selo por rede, colorido só quando ativo.
             Em telas estreitas vira uma grade de duas colunas. -->
        <nav
          class="integracoes-social__mobile-navigation relative flex h-12 flex-shrink-0 gap-2 overflow-x-auto pb-1 max-md:grid max-md:h-auto max-md:grid-cols-2 max-md:overflow-visible max-md:pb-0 md:sticky md:top-4 md:h-auto md:w-44 md:flex-col md:overflow-visible md:pb-0"
        >
          <button
            v-for="section in socialSections"
            :key="section.channel.key"
            type="button"
            class="integracoes-social__mobile-navigation-item group relative z-10 flex h-12 w-36 flex-shrink-0 items-center gap-2.5 rounded-2xl border border-transparent px-2.5 text-left transition-all duration-500 max-md:w-full max-md:min-w-0 md:w-full motion-reduce:transition-none"
            :class="
              activeSocialKey === section.channel.key
                ? 'border-white/20'
                : 'hover:border-n-weak hover:bg-n-alpha-1'
            "
            @click="selectSocialChannel(section.channel.key)"
          >
            <Transition
              enter-active-class="transition-opacity duration-200 ease-out motion-reduce:transition-none"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-150 ease-in motion-reduce:transition-none"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <span
                v-if="activeSocialKey === section.channel.key"
                aria-hidden="true"
                class="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl border border-white/25 bg-white/15 shadow-[0_6px_16px_rgba(15,23,42,0.12)] backdrop-blur-sm"
              >
                <span
                  class="absolute inset-0 opacity-35"
                  :class="section.channel.gradient"
                />
                <span
                  class="absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"
                />
                <span
                  class="absolute -bottom-5 -right-2 size-12 rounded-full bg-white/35 blur-xl"
                />
              </span>
            </Transition>
            <span
              class="relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm text-white shadow-md transition-all duration-300 group-hover:scale-110 motion-reduce:transition-none"
              :class="[
                section.channel.gradient,
                activeSocialKey === section.channel.key
                  ? 'scale-110 ring-2 ring-white/50'
                  : 'opacity-75 saturate-50',
              ]"
            >
              <i :class="section.channel.icon" />
            </span>
            <span
              class="relative z-10 whitespace-nowrap text-sm font-medium transition-colors duration-200 motion-reduce:transition-none"
              :class="
                activeSocialKey === section.channel.key
                  ? 'text-n-slate-12'
                  : 'text-n-slate-11'
              "
            >
              {{ section.channel.label }}
            </span>
          </button>
        </nav>

        <div class="min-w-0 flex-1">
          <Transition
            mode="out-in"
            enter-active-class="transition duration-300 ease-out motion-reduce:transition-none"
            enter-from-class="translate-y-2 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-200 ease-in motion-reduce:transition-none"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="-translate-y-1 opacity-0"
          >
            <!-- No telemovel so se ve um canal de cada vez, e a fila de cima ja
                 diz qual e: esta moldura nao esta a agrupar nada que nao esteja
                 evidente, e custa 16px de cada lado. O titulo fica, a caixa sai. -->
            <section
              v-if="activeSocialSection"
              :key="activeSocialSection.channel.key"
              class="rounded-2xl border border-n-weak bg-n-alpha-1 p-4 shadow-sm max-md:border-0 max-md:bg-transparent max-md:p-0 max-md:shadow-none"
            >
              <h3
                class="mb-2.5 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
              >
                <span
                  class="flex h-6 w-6 items-center justify-center rounded-full text-xs text-white shadow-sm"
                  :class="activeSocialSection.channel.gradient"
                >
                  <i :class="activeSocialSection.channel.icon" />
                </span>
                {{ activeSocialSection.channel.label }}
              </h3>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ChannelConnectionCard
                  v-for="card in activeSocialSection.cards"
                  :key="card.key"
                  :icon="card.icon"
                  :icon-gradient="card.iconGradient"
                  :title="card.title"
                  :subtitle="card.subtitle"
                  subtitle-icon="i-lucide-at-sign"
                  :status-label="card.statusLabel"
                  :status-class="card.statusClass"
                  :badges="card.badges"
                  :details="card.details"
                  :meta-line="card.metaLine"
                  :notice="card.notice"
                  :muted="card.muted"
                >
                  <template #menu>
                    <div
                      v-if="card.wame"
                      v-on-clickaway="() => closeSocialCardMenu(card)"
                      class="relative flex items-center"
                    >
                      <button
                        type="button"
                        aria-label="Mais ações do canal"
                        class="flex h-8 w-8 items-center justify-center border-none bg-transparent p-0 text-n-slate-11 transition-colors hover:text-n-slate-12"
                        @click="toggleSocialCardMenu(card)"
                      >
                        <i class="i-lucide-ellipsis-vertical text-xl" />
                      </button>
                      <DropdownMenu
                        v-if="openCardMenuId === socialCardMenuKey(card)"
                        :menu-items="socialCardMenuItems(card)"
                        class="top-full z-50 mt-1 ltr:right-0 rtl:left-0"
                        @action="handleSocialCardMenuAction($event, card)"
                      />
                    </div>
                  </template>

                  <!-- Canal WAME já existente: as ações são as do estado real. -->
                  <template v-if="card.wame">
                    <Button
                      v-if="
                        ['pending', 'not_started', 'disconnected'].includes(
                          card.wame.status
                        ) ||
                        (card.wame.reauthorization_required &&
                          card.wame.status !== 'connected')
                      "
                      size="sm"
                      :label="
                        card.wame.reauthorization_required
                          ? 'Refazer autorização'
                          : 'Autorizar na Meta'
                      "
                      icon="i-lucide-shield-check"
                      :is-loading="
                        actionLoading[`wame-auth-${card.wame.inbox_id}`]
                      "
                      @click="authorizeWameChannel(card)"
                    />
                    <Button
                      v-if="['pending', 'awaiting'].includes(card.wame.status)"
                      slate
                      size="sm"
                      label="Verificar agora"
                      icon="i-lucide-refresh-cw"
                      :is-loading="
                        actionLoading[`wame-sync-${card.wame.inbox_id}`]
                      "
                      @click="verifyWameChannel(card)"
                    />
                    <router-link
                      v-if="card.wame.status === 'connected'"
                      :to="
                        accountScopedRoute('settings_inbox_show', {
                          inboxId: card.wame.inbox_id,
                        })
                      "
                    >
                      <Button
                        slate
                        size="sm"
                        label="Abrir caixa de entrada"
                        icon="i-lucide-settings-2"
                      />
                    </router-link>
                  </template>

                  <!-- LinkedIn: OAuth direto, sem WAME e sem caixa de entrada.
                       Sem permissão (ou sem app configurado) o card só informa. -->
                  <template v-else-if="card.linkedin">
                    <template
                      v-if="viewerCanManageLinkedin && card.linkedin.configured"
                    >
                      <Button
                        size="sm"
                        :slate="card.linkedin.connected"
                        :label="
                          card.linkedin.has_saved_profile
                            ? 'Reconectar LinkedIn'
                            : 'Conectar LinkedIn'
                        "
                        :icon="
                          card.linkedin.has_saved_profile
                            ? 'i-lucide-refresh-cw'
                            : card.channel.icon
                        "
                        :is-loading="Boolean(actionLoading['linkedin-connect'])"
                        @click="connectLinkedin"
                      />
                      <Button
                        v-if="card.linkedin.has_saved_profile"
                        ruby
                        faded
                        size="sm"
                        label="Desconectar"
                        icon="i-lucide-unplug"
                        :is-loading="
                          Boolean(actionLoading['linkedin-disconnect'])
                        "
                        @click="requestLinkedinDisconnect(card)"
                      />
                    </template>
                  </template>

                  <!-- Rede WAME ainda sem conta: o fluxo inteiro é o modal.
                       Só para quem administra: provisionar o canal exige
                       administrador no Zoraxy (`check_admin_authorization?` em
                       Wame::SocialChannelsController). Sem esta guarda o
                       vendedor percorria duas etapas do assistente para levar um
                       403 no fim — o backend já barrava, mas só depois. -->
                  <Button
                    v-else-if="card.wameProvider && viewerCanCreate"
                    size="sm"
                    class="integracoes-social__connect"
                    :label="`Conectar ${card.channel.label}`"
                    :icon="card.channel.icon"
                    @click="openSocialWizard(card.wameProvider)"
                  />

                  <!-- Redes ainda administradas pelo Aceleres. -->
                  <router-link
                    v-else
                    :to="
                      accountScopedRoute('settings_inboxes_page_channel', {
                        sub_page: card.channel.route,
                      })
                    "
                  >
                    <Button
                      slate
                      size="sm"
                      label="Abrir configuração do canal"
                      icon="i-lucide-external-link"
                    />
                  </router-link>
                </ChannelConnectionCard>
              </div>
            </section>
          </Transition>
        </div>
      </div>

      <SocialConnectionWizard
        ref="socialWizardRef"
        class="integracoes-social__wizard"
        :teams="teamOptions"
        :existing-channels="reusableSocialInboxes"
        :is-loading="Boolean(actionLoading['social-connect'])"
        :authorization-url="wameAuthUrl"
        :error-message="socialWizardError"
        @connect="connectSocialChannel"
        @use-existing="useExistingSocialInbox"
        @verify="verifySocialWizardChannel"
        @authorized="loadAuxiliaryIntegrations"
      />

      <Dialog
        ref="instagramIdentityDialog"
        title="Identificar conta do Instagram"
        description="A WAME não informa qual @ foi autorizado. Confira o perfil no Instagram antes de salvar este nome no Zoraxy."
        confirm-button-label="Salvar identificação"
        cancel-button-label="Cancelar"
        width="md"
        :is-loading="
          Boolean(
            instagramIdentityCard &&
              actionLoading[
                `instagram-identity-${instagramIdentityCard.wame.inbox_id}`
              ]
          )
        "
        :disable-confirm-button="!instagramIdentityProfileUrl"
        @confirm="saveInstagramIdentity"
        @close="instagramIdentityError = ''"
      >
        <div class="flex flex-col gap-2">
          <label
            for="instagram-account-username"
            class="text-sm font-medium text-n-slate-12"
          >
            Usuário do Instagram
          </label>
          <div
            class="flex h-11 items-center rounded-xl border border-n-weak bg-n-solid-1 px-3 focus-within:border-n-brand"
          >
            <span class="text-sm text-n-slate-10">@</span>
            <input
              id="instagram-account-username"
              v-model="instagramIdentityUsername"
              type="text"
              maxlength="31"
              autocomplete="off"
              spellcheck="false"
              placeholder="nomedaconta"
              class="reset-base m-0 h-full min-w-0 flex-1 border-0 bg-transparent px-1 text-sm text-n-slate-12 outline-none"
              @input="instagramIdentityError = ''"
            />
          </div>
          <p v-if="instagramIdentityError" class="text-xs text-n-ruby-11">
            {{ instagramIdentityError }}
          </p>
          <a
            v-if="instagramIdentityProfileUrl"
            :href="instagramIdentityProfileUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
          >
            Conferir este perfil no Instagram
            <i class="i-lucide-external-link size-3" />
          </a>
        </div>
      </Dialog>
    </section>

    <section
      v-if="activeTab === 'email'"
      class="rounded-3xl border border-n-weak bg-n-solid-1 p-5 shadow-sm max-md:p-3"
    >
      <h2 class="mb-4 text-xl font-semibold text-n-slate-12">E-mail</h2>
      <EmailIntegrationPanel :channel="EMAIL_CHANNEL" @feedback="setFeedback" />
    </section>

    <section
      v-if="canSeeApps"
      v-show="activeTab === 'apps'"
      class="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-n-weak bg-n-alpha-2 p-5"
    >
      <div>
        <h2 class="font-semibold text-n-slate-12">Apps e automações</h2>
        <p class="mt-1 text-sm text-n-slate-10">
          Gerencie Slack, Linear, Notion, Shopify e webhooks na central de
          aplicativos do Zoraxy.
        </p>
      </div>
      <router-link :to="accountScopedRoute('settings_applications')">
        <Button slate label="Abrir apps e webhooks" icon="i-lucide-blocks" />
      </router-link>
    </section>
  </main>
</template>

<style>
/*
  Aceleres: com a saida do zoom global 0.8, a tipografia desta pagina ficou
  grande demais. Em vez de reescrever ~60 utilitarios no template, reduzimos
  a escala em 12,5% dentro do escopo da pagina (o cabecalho e teleportado,
  por isso os dois prefixos). Especificidade (0,2,0) vence o utilitario do
  Tailwind (0,1,0), sem vazar para o resto do dashboard.
*/
.integracoes-page .text-xs,
.integracoes-page__header .text-xs {
  font-size: 0.65625rem;
  line-height: 0.875rem;
}

.integracoes-page .text-sm,
.integracoes-page__header .text-sm {
  font-size: 0.765625rem;
  line-height: 1.09375rem;
}

.integracoes-page .text-base,
.integracoes-page__header .text-base {
  font-size: 0.875rem;
  line-height: 1.3125rem;
}

.integracoes-page .text-lg,
.integracoes-page__header .text-lg {
  font-size: 0.984375rem;
  line-height: 1.53125rem;
}

.integracoes-page .text-xl,
.integracoes-page__header .text-xl {
  font-size: 1.09375rem;
  line-height: 1.53125rem;
}

.integracoes-page .text-2xl,
.integracoes-page__header .text-2xl {
  font-size: 1.3125rem;
  line-height: 1.75rem;
}
</style>

<style>
/*
  Painel do modal "Gerenciar conexao". O Dialog padrao pinta o corpo com
  bg-n-alpha-3 + backdrop-blur-[100px]; aqui o conteudo e denso (formulario,
  equipes, grupos) e o vidro fosco deixava o texto disputando com a tela de
  tras. Fundo solido padrao, sem blur. O escurecimento do overlay continua
  sendo o do Dialog.
*/
form.integracoes-whatsapp__settings-panel {
  background-color: rgb(var(--solid-2));
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}
</style>
